"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Hash, MessageSquare, Send, Users, Search, Smile, Loader2, Paperclip, Mic, FolderOpen, X, BellOff, Bell, Pin, Trophy, BarChart3, DollarSign, PhoneCall, Calendar, TrendingUp, FileText, UserCheck, Target, CornerDownRight, ChevronDown, Phone, Bookmark, Forward, Archive, Lock, Megaphone, Play } from "lucide-react";
// Virtualizer removed — normal DOM flow handles dynamic heights (images, attachments, read receipts) correctly
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/app/_module/components/ui/dialog";
import { Sheet, SheetContent } from "@/app/_module/components/ui/sheet";
import { teamChatService, setTeamChatAgencyOverride, type Channel, type Message, type ChannelMember } from "./_modules/components/TeamChatService";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";

// ── Extracted components ─────────────────────────────────────────────────────
import { ResourcePicker } from "./_modules/components/ResourcePicker";
import { RecordingPicker } from "./_modules/components/RecordingPicker";
import { MEDIA_LOADED_EVENT } from "./_modules/components/helpers";
import { MediaPicker } from "./_modules/components/MediaPicker";
import { TeamChatSkeleton, MessageListSkeleton } from "./_modules/components/TeamChatSkeleton";
import { VoiceRecorder } from "./_modules/components/VoiceRecorder";
import { MessageItem } from "./_modules/components/MessageItem";
import { ChannelSidebar } from "./_modules/components/ChannelSidebar";
import { TypingIndicator } from "./_modules/components/TypingIndicator";
import { MentionAutocomplete, type AgencyMentionOption } from "./_modules/components/MentionAutocomplete";
import { MemberList } from "./_modules/components/MemberList";
import { isDemoMode } from "@/app/_module/utils/demoMode";
import { removeRealtimeChannel } from "../../_module/services/realtimeTeardown";

// ── Types for virtual list items ─────────────────────────────────────────────
type VirtualListItem =
  | { type: 'date'; label: string; key: string }
  | { type: 'message'; msg: Message; key: string }
  | { type: 'new-messages'; key: string };

// ── Virtualized Message List ────────────────────────────────────────────────
function VirtualizedMessageList({
  messages,
  searchQuery,
  searchResults,
  messagesLoading,
  messagesContainerRef,
  userId,
  activeChannelId,
  queryClient,
  reactMutation,
  deleteMutation,
  editMutation,
  onReply,
  onViewThread,
  onBookmark,
  onForward,
  bookmarkedIds,
  readReceipts = {},
  allMembers = [],
  isAdminOrOwner = false,
  unreadCount = 0,
  highlightedMessageId = null,
  onSendGifReply,
  hasOlder = false,
  loadingOlder = false,
  onLoadOlder,
}: {
  messages: Message[];
  searchQuery: string;
  searchResults: Message[];
  messagesLoading: boolean;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  userId: string;
  activeChannelId: string | null;
  queryClient: any;
  reactMutation: any;
  deleteMutation: any;
  editMutation: any;
  onReply: (msg: Message) => void;
  onViewThread: (msgId: string) => void;
  onBookmark?: (msg: Message) => void;
  onForward?: (msg: Message) => void;
  bookmarkedIds?: Set<string>;
  readReceipts?: Record<string, string>;
  allMembers?: Array<{ userId: string; name: string; profileImageUrl?: string }>;
  isAdminOrOwner?: boolean;
  unreadCount?: number;
  highlightedMessageId?: string | null;
  onSendGifReply?: (gifUrl: string, parentMessageId: string) => void;
  hasOlder?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
}) {
  const displayMessages = searchQuery.trim() && searchResults.length > 0 ? searchResults : messages;
  const sorted = useMemo(() => [...displayMessages].reverse(), [displayMessages]);

  // Find the last message sent by the current user (for read receipts)
  const lastOwnMessageId = useMemo(() => {
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (sorted[i].senderId === userId) return sorted[i].id;
    }
    return null;
  }, [sorted, userId]);

  // Build combined list of date separators + messages + new-messages divider
  const items: VirtualListItem[] = useMemo(() => {
    const result: VirtualListItem[] = [];
    let lastDate = "";
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    // The divider goes before the last `unreadCount` messages (only from other users count toward unread)
    // Place divider at index: sorted.length - unreadCount
    const dividerIndex = unreadCount > 0 && unreadCount < sorted.length ? sorted.length - unreadCount : -1;
    let dividerInserted = false;

    for (let i = 0; i < sorted.length; i++) {
      const msg = sorted[i];
      const msgDate = new Date(msg.createdAt).toDateString();
      if (msgDate !== lastDate) {
        const dateLabel = msgDate === today ? "Today" : msgDate === yesterday ? "Yesterday" : new Date(msg.createdAt).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        result.push({ type: 'date', label: dateLabel, key: `date-${msgDate}` });
        lastDate = msgDate;
      }
      // Insert "New Messages" divider before the first unread message
      if (!dividerInserted && dividerIndex >= 0 && i === dividerIndex) {
        result.push({ type: 'new-messages', key: 'new-messages-divider' });
        dividerInserted = true;
      }
      result.push({ type: 'message', msg, key: msg.id });
    }
    return result;
  }, [sorted, unreadCount]);

  const bottomRef = useRef<HTMLDivElement>(null);

  // Follow the live end of the channel.
  //
  // A one-shot `scrollTop = scrollHeight` always lands short: avatars, GIFs,
  // stickers, link embeds and sales-win cards measure ZERO height until they
  // decode, so the content grows underneath us afterwards and the view is left
  // parked mid-history — or, for something you just sent, just above it.
  //
  // A timed re-pin does not fix that either: an image can take longer than any
  // window we pick, and tying the window to an effect means the very thing that
  // signals "content changed" (items.length) also cancels it. So instead we
  // watch the content box and re-stick whenever it GROWS, for as long as the
  // reader is still at the bottom.
  const stickToBottom = useCallback(() => {
    // scrollIntoView on the end sentinel, not scrollTop on the container:
    // it scrolls whatever ancestor actually scrolls, so this keeps working
    // regardless of where the overflow ends up in the flex chain. The
    // scrollTop assignment stays as a belt-and-braces second pass.
    bottomRef.current?.scrollIntoView({ block: "end" });
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messagesContainerRef]);

  // A GIF or sticker takes its real height only once it decodes — after the
  // scroll that was meant to reveal it. The image tells us when that happens.
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const onMedia = () => { if (nearBottomRef.current) stickToBottom(); };
    el.addEventListener(MEDIA_LOADED_EVENT, onMedia);
    return () => el.removeEventListener(MEDIA_LOADED_EVENT, onMedia);
  }, [messagesContainerRef, stickToBottom, activeChannelId, messagesLoading]);

  // Is the reader at the live end of the channel? Updated on scroll so a new
  // message can follow them down without yanking someone reading history.
  const nearBottomRef = useRef(true);
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  }, [messagesContainerRef]);

  // Callback ref, not useRef + useEffect: while the skeleton is showing this
  // node does not exist, and an effect keyed on the channel would not re-run
  // when it finally mounts.
  const contentObserverRef = useRef<ResizeObserver | null>(null);
  const contentHeightRef = useRef(0);
  const contentRef = useCallback((node: HTMLDivElement | null) => {
    contentObserverRef.current?.disconnect();
    contentObserverRef.current = null;
    if (!node || typeof ResizeObserver === "undefined") return;
    contentHeightRef.current = node.scrollHeight;
    const observer = new ResizeObserver(() => {
      const grew = node.scrollHeight > contentHeightRef.current;
      contentHeightRef.current = node.scrollHeight;
      if (grew && nearBottomRef.current) stickToBottom();
    });
    observer.observe(node);
    contentObserverRef.current = observer;
  }, [stickToBottom]);

  useEffect(() => () => contentObserverRef.current?.disconnect(), []);

  // When older pages load, messages PREPEND — preserve the reading position
  // (and suppress the scroll-to-bottom) instead of yanking the user down.
  const preserveScrollRef = useRef<{ prevH: number; prevTop: number } | null>(null);
  const handleLoadOlder = useCallback(() => {
    const el = messagesContainerRef.current;
    if (el) preserveScrollRef.current = { prevH: el.scrollHeight, prevTop: el.scrollTop };
    onLoadOlder?.();
  }, [onLoadOlder]);

  // Discord-style infinite scrollback: auto-load the next page when the user
  // scrolls within ~400px of the top. One page per trigger; the observer
  // re-arms after each load, so holding the scrollbar at the top walks back
  // through history smoothly without ever fetching the whole channel at once.
  const topSentinelRef = useRef<HTMLDivElement>(null);
  // Set once we have landed at the bottom for this channel. Until then the
  // container is still at scrollTop 0, which sits inside the sentinel's 400px
  // margin — arming the observer there fires an older-page load on every
  // refresh and walks the view backwards through history.
  const [atBottomOnce, setAtBottomOnce] = useState(false);
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const root = messagesContainerRef.current;
    if (!atBottomOnce) return;
    if (!sentinel || !root || !hasOlder || !onLoadOlder || loadingOlder || searchQuery.trim()) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handleLoadOlder();
      },
      { root, rootMargin: "400px 0px 0px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [atBottomOnce, hasOlder, loadingOlder, onLoadOlder, handleLoadOlder, activeChannelId, searchQuery]);

  // Anything YOU send returns you to the live end, wherever you were reading.
  // Without this, scrolling up to look at something earlier and then posting
  // leaves your own message off-screen: the "are they near the bottom?" gate
  // that protects a reader from being yanked around also blocks the one case
  // where being moved is exactly what you asked for.
  const prevOwnRef = useRef(lastOwnMessageId);
  useEffect(() => {
    if (!lastOwnMessageId || lastOwnMessageId === prevOwnRef.current) return;
    prevOwnRef.current = lastOwnMessageId;
    nearBottomRef.current = true;
    stickToBottom();
    requestAnimationFrame(stickToBottom);
  }, [lastOwnMessageId, stickToBottom]);

  const prevCountRef = useRef(items.length);
  useEffect(() => {
    if (items.length > 0 && items.length !== prevCountRef.current) {
      prevCountRef.current = items.length;
      if (preserveScrollRef.current) {
        const { prevH, prevTop } = preserveScrollRef.current;
        preserveScrollRef.current = null;
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = prevTop + (el.scrollHeight - prevH);
        return;
      }
      // Only follow the reader down if they are already at the live end; the
      // ResizeObserver above catches the media that has not decoded yet.
      if (!nearBottomRef.current) return;
      stickToBottom();
      requestAnimationFrame(stickToBottom);
    }
  }, [items.length, stickToBottom]);

  // Scroll to bottom on initial load / channel switch.
  //
  // This used to depend on [activeChannelId] alone. On a refresh the channel id
  // resolves BEFORE the first page of messages arrives, so `items.length > 0`
  // was false, nothing was scheduled, and the effect never re-ran — leaving the
  // view wherever the browser put it. Track the channel we've landed in
  // instead, so the scroll happens when the messages actually exist.
  const landedChannelRef = useRef<string | null>(null);

  // A channel switch has to land again. Declared BEFORE the landing effect so
  // the reset runs first on a switch — otherwise the landing effect can claim
  // the new channel while the old channel's messages are still on screen.
  useEffect(() => {
    if (landedChannelRef.current && landedChannelRef.current !== activeChannelId) {
      landedChannelRef.current = null;
      setAtBottomOnce(false);
    }
  }, [activeChannelId]);

  useEffect(() => {
    if (!activeChannelId) return;
    if (landedChannelRef.current === activeChannelId) return;
    if (items.length === 0) return;
    landedChannelRef.current = activeChannelId;
    nearBottomRef.current = true;
    stickToBottom();
    // Again after paint — the first pass runs before the browser has laid the
    // new rows out, so scrollHeight is still the old one.
    requestAnimationFrame(stickToBottom);
    setAtBottomOnce(true);
  }, [activeChannelId, items.length, stickToBottom]);


  // Scroll to highlighted message (from pinned click)
  useEffect(() => {
    if (!highlightedMessageId) return;
    const el = document.getElementById(`msg-${highlightedMessageId}`);
    if (el) {
      const timer = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [highlightedMessageId]);

  if (messagesLoading) {
    return (
      <div className="flex-1 overflow-y-auto flex flex-col" ref={messagesContainerRef}>
        <MessageListSkeleton />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center text-center" ref={messagesContainerRef}>
        <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center mb-4">
          <MessageSquare className="w-8 h-8 text-blue-500" />
        </div>
        <h3 className="text-lg font-semibold text-zinc-800 dark:text-zinc-200 mb-1">
          No messages yet
        </h3>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-xs">
          Start the conversation! Send the first message to your team.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto flex flex-col" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
      {/* Spacer pushes messages to bottom when content is shorter than viewport (Discord/Slack style) */}
      <div className="flex-1" />
      {hasOlder && !searchQuery.trim() && <div ref={topSentinelRef} className="h-px" />}
      {loadingOlder && (
        <div className="flex items-center justify-center gap-2 py-3 text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-[11px] font-medium">Loading history…</span>
        </div>
      )}
      <div ref={contentRef}>
        {items.map(item => {
          if (!item) return null;

          if (item.type === 'new-messages') {
            return (
              <div key={item.key}>
                <div className="flex items-center gap-3 px-4 py-2">
                  <div className="flex-1 h-px bg-red-400" />
                  <span className="text-xs font-semibold text-red-400">New Messages</span>
                  <div className="flex-1 h-px bg-red-400" />
                </div>
              </div>
            );
          }

          if (item.type === 'date') {
            return (
              <div key={item.key}>
                <div className="flex items-center gap-3 px-4 py-3 mt-2">
                  <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
                  <span className="text-[11px] font-semibold text-zinc-400 dark:text-zinc-500 whitespace-nowrap">
                    {item.label}
                  </span>
                  <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
                </div>
              </div>
            );
          }

          const msg = item.msg;
          return (
            <div key={item.key} id={`msg-${msg.id}`}>
              <MessageItem
                message={msg}
                isOwn={msg.senderId === userId}
                currentUserId={userId}
                onReact={emoji => {
                  const existingReaction = (msg.reactions || []).find(
                    (r: any) => r.emoji === emoji && r.userId === userId
                  );
                  if (existingReaction) {
                    teamChatService.removeReaction(msg.id, emoji).then(() => {
                      queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] });
                    });
                  } else {
                    reactMutation.mutate({ messageId: msg.id, emoji });
                  }
                }}
                onDelete={() => deleteMutation.mutate(msg.id)}
                onEdit={content => editMutation.mutate({ messageId: msg.id, content })}
                onPin={async () => {
                  const isPinned = (msg.metadata as any)?.pinned;
                  try {
                    if (isPinned) {
                      await teamChatService.unpinMessage(msg.id);
                    } else {
                      await teamChatService.pinMessage(msg.id);
                    }
                    queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] });
                  } catch {}
                }}
                onReply={() => onReply(msg)}
                onViewThread={() => onViewThread(msg.parentMessageId || msg.id)}
                onBookmark={onBookmark ? () => onBookmark(msg) : undefined}
                onForward={onForward ? () => onForward(msg) : undefined}
                onSendGif={onSendGifReply ? (gifUrl) => onSendGifReply(gifUrl, msg.id) : undefined}
                isBookmarked={bookmarkedIds?.has(msg.id)}
                readReceipts={readReceipts}
                allMembers={allMembers}
                isLastOwnMessage={msg.id === lastOwnMessageId}
                searchHighlight={searchQuery || undefined}
                isAdminOrOwner={isAdminOrOwner}
                isHighlighted={msg.id === highlightedMessageId}
              />
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function TeamChatPage() {
  const { user } = useUserContext();
  const userId = (user as any)?.user?.id || (user as any)?.id;
  const agencyName = (user as any)?.currentAgency?.agency?.name || (user as any)?.currentAgency?.name;
  const { branding } = useBranding();
  const queryClient = useQueryClient();

  const [chatAgencyId, setChatAgencyId] = useState<string | null>(null); // Which agency's chat to show (null = current)
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState("");
  // Drafts are stored in sessionStorage so they survive page refresh within the same tab
  const [showMembers, setShowMembers] = useState(true);
  // Discord-iOS-style right drawer: below lg the members pane is display:none,
  // so the Users button opens the member list as a right-side sheet instead.
  const [mobileMembersOpen, setMobileMembersOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Message[]>([]);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelDesc, setNewChannelDesc] = useState("");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [resourcePickerOpen, setResourcePickerOpen] = useState(false);
  const [recordingPickerOpen, setRecordingPickerOpen] = useState(false);
  const [ephemeralCard, setEphemeralCard] = useState<string | null>(null); // Private command result (only you see)
  const [typingUsers, setTypingUsers] = useState<string[]>([]); // Names of users currently typing
  const [presenceMap, setPresenceMap] = useState<Record<string, { status: string; lastSeen?: string }>>({}); // userId -> presence
  const [mentionSearch, setMentionSearch] = useState<{ query: string; startIndex: number } | null>(null);
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  // Thread panel state
  const [threadParentId, setThreadParentId] = useState<string | null>(null);
  const [threadInput, setThreadInput] = useState("");
  const [replyingTo, setReplyingTo] = useState<{ id: string; senderName: string; content: string } | null>(null);
  const [showPinnedBar, setShowPinnedBar] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [channelMuted, setChannelMuted] = useState(false);
  // Bookmarks (localStorage-backed)
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [bookmarks, setBookmarks] = useState<Array<{ id: string; content: string; senderName: string; channelName: string; channelId: string; createdAt: string }>>([]);
  // Forward dialog
  const [forwardingMessage, setForwardingMessage] = useState<Message | null>(null);
  // Archive
  const [isChannelArchived, setIsChannelArchived] = useState(false);

  // Track mentioned users so we can convert @Name -> <@userId:Name> on send
  const mentionedUsersRef = useRef<Map<string, string>>(new Map()); // displayName -> userId

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingFilePreview, setPendingFilePreview] = useState<string | null>(null);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const socketRef = useRef<any>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingEmitRef = useRef(0);
  const sidebarSearchRef = useRef<HTMLInputElement>(null);

  // ── Notification sound (Web Audio API beep) ──────────────────────────────
  const playNotificationSound = useCallback(() => {
    try {
      const ctx = new AudioContext();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gain.gain.value = 0.1;
      oscillator.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch {}
  }, []);

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: channels = [], isLoading: channelsLoading } = useQuery({
    queryKey: ["team-chat", "channels"],
    queryFn: () => teamChatService.getChannels(),
    refetchInterval: 15000,
  });

  const activeChannel = channels.find(c => c.channel.id === activeChannelId);

  const { data: messagesPages, isLoading: messagesLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["team-chat", "messages", activeChannelId],
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      activeChannelId ? teamChatService.getMessages(activeChannelId, pageParam, 50) : Promise.resolve({ messages: [], hasMore: false }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: any) => lastPage?.hasMore ? lastPage.nextCursor : undefined,
    enabled: !!activeChannelId && channels.some(c => c.channel.id === activeChannelId),
    refetchInterval: 60000,
  });

  // Flatten all pages into a single messages array (oldest first across pages)
  const messages: Message[] = useMemo(() => {
    if (!messagesPages?.pages) return [];
    // Pages come newest-first; flatten and dedupe by id
    const all: Message[] = [];
    const seen = new Set<string>();
    for (const page of messagesPages.pages) {
      for (const msg of (page?.messages || [])) {
        if (!seen.has(msg.id)) { seen.add(msg.id); all.push(msg); }
      }
    }
    return all;
  }, [messagesPages]);

  const { data: members = [] } = useQuery({
    queryKey: ["team-chat", "members", activeChannelId],
    queryFn: () => activeChannelId ? teamChatService.getMembers(activeChannelId) : Promise.resolve([]),
    enabled: !!activeChannelId && channels.length > 0 && channels.some(c => c.channel.id === activeChannelId),
    refetchInterval: 30000,
  });

  // Get all agency members for DM sidebar (use General channel which has everyone)
  const generalChannelId = channels.find(c => c.channel.slug === "general")?.channel.id;
  const { data: allAgencyMembers = [] } = useQuery({
    queryKey: ["team-chat", "all-members", generalChannelId],
    queryFn: () => generalChannelId ? teamChatService.getMembers(generalChannelId) : Promise.resolve([]),
    enabled: !!generalChannelId,
    staleTime: 60000,
  });

  // Thread messages query
  const { data: threadData } = useQuery({
    queryKey: ["team-chat", "thread", threadParentId],
    queryFn: () => threadParentId ? teamChatService.getThread(threadParentId) : null,
    enabled: !!threadParentId,
    refetchInterval: 15000,
  });

  const threadParentMessage = threadParentId ? messages.find(m => m.id === threadParentId) : null;

  // Read receipts query
  const { data: readReceipts = {} } = useQuery({
    queryKey: ["team-chat", "read-receipts", activeChannelId],
    queryFn: () => activeChannelId ? teamChatService.getReadReceipts(activeChannelId) : Promise.resolve({}),
    enabled: !!activeChannelId && channels.length > 0 && channels.some(c => c.channel.id === activeChannelId),
    refetchInterval: 30000,
  });

  // Pinned messages for current channel
  const { data: pinnedMessages = [] } = useQuery({
    queryKey: ["team-chat", "pinned", activeChannelId],
    queryFn: () => activeChannelId ? teamChatService.getPinnedMessages(activeChannelId) : Promise.resolve([]),
    enabled: !!activeChannelId && channels.length > 0 && channels.some(c => c.channel.id === activeChannelId),
    staleTime: 30000,
  });

  // Track muted state for current channel from members data
  useEffect(() => {
    const myMembership = members.find(m => m.userId === userId);
    setChannelMuted(!!myMembership?.isMuted);
  }, [members, userId]);

  // Track archived state for current channel
  useEffect(() => {
    setIsChannelArchived(!!activeChannel?.channel.description?.startsWith('[ARCHIVED]'));
  }, [activeChannel]);

  // Load bookmarks from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('team-chat-bookmarks');
      if (stored) setBookmarks(JSON.parse(stored));
    } catch {}
  }, []);

  const bookmarkedIds = useMemo(() => new Set(bookmarks.map(b => b.id)), [bookmarks]);

  const handleBookmark = useCallback((msg: Message) => {
    setBookmarks(prev => {
      const exists = prev.some(b => b.id === msg.id);
      let next: typeof prev;
      if (exists) {
        next = prev.filter(b => b.id !== msg.id);
      } else {
        next = [...prev, {
          id: msg.id,
          content: (msg.content || '').slice(0, 200),
          senderName: msg.sender?.name || msg.sender?.email || 'Unknown',
          channelName: activeChannel?.channel.name || '',
          channelId: msg.channelId,
          createdAt: msg.createdAt,
        }];
      }
      localStorage.setItem('team-chat-bookmarks', JSON.stringify(next));
      return next;
    });
  }, [activeChannel]);

  const handleForward = useCallback((msg: Message) => {
    setForwardingMessage(msg);
  }, []);

  // Fetch ALL agencies the user belongs to + their sub-agency trees
  const userRole = (
    (user as any)?.currentAgency?.role ||
    (user as any)?.user?.agencyRole ||
    (user as any)?.user?.currentAgencyUser?.role ||
    (user as any)?.currentAgencyUser?.role ||
    (user as any)?.role ||
    // Fallback: check agencyUsers array for matching agency
    ((user as any)?.user?.agencyUsers || (user as any)?.agencyUsers || [])
      .find((au: any) => au.agencyId === ((user as any)?.currentAgency?.agency?.id || (user as any)?.currentAgency?.agencyId))
      ?.role ||
    ""
  ).toLowerCase();
  const isOwnerOrAdmin = ["owner", "admin"].includes(userRole);
  // DM display name — resolve from allMembers via slug, with self-DM detection
  const getDmName = useCallback((ch?: Channel | null) => {
    if (!ch || ch.channel.type !== 'dm') return ch?.channel.name || '';
    const slug = ch.channel.slug || '';

    // Self-DM check: slug is just "dm-{myUserId}"
    if (userId && slug.replace('dm-', '') === userId) {
      return 'Personal Notes';
    }

    // Look up other person using full UUID match
    if (userId && slug && allAgencyMembers.length > 0) {
      for (const member of allAgencyMembers) {
        if (member.userId === userId) continue;
        if (slug.includes(member.userId)) {
          return member.user.name || member.user.email;
        }
      }
      // Fallback: 30-char prefix match for truncated slugs
      for (const member of allAgencyMembers) {
        if (member.userId === userId) continue;
        if (slug.includes(member.userId.slice(0, 30))) {
          return member.user.name || member.user.email;
        }
      }
    }

    // Fallback: parse "Name1 & Name2"
    const names = ch.channel.name.split(' & ');
    const myName = (user as any)?.user?.name || (user as any)?.name;
    if (myName) {
      const other = names.find((n: string) => n !== myName);
      if (other) return other;
    }
    return names[1] || names[0] || 'Direct Message';
  }, [userId, allAgencyMembers, user]);

  const isAnnouncementChannel = !!activeChannel?.channel.isAnnouncement;
  const currentAgencyObj = (user as any)?.currentAgency?.agency || (user as any)?.currentAgency;

  // Logo resolved after agencyList is built (uses effective chat agency)

  const { data: allUserAgencies = [] } = useQuery({
    queryKey: ["team-chat", "user-agencies"],
    queryFn: async () => {
      try {
        const { getSupabaseSession } = await import("@/app/_module/services/auth");
        const { getApiUrl } = await import("@/app/_module/services/config");
        const AgencyTokenMgr = (await import("@/app/_module/services/agencyTokenManager")).default;
        const session = await getSupabaseSession();
        const agencyId = AgencyTokenMgr.getCurrentAgencyId();
        const headers: any = {
          Authorization: `Bearer ${session?.access_token}`,
          "Content-Type": "application/json",
          ...(agencyId ? { "x-agency-id": agencyId } : {}),
        };

        // 1. Fetch all agencies the user is a member of
        const myAgenciesRes = await fetch(getApiUrl("agencies/auth/agencies"), { headers });
        let myAgencies: any[] = [];
        if (myAgenciesRes.ok) {
          const json = await myAgenciesRes.json();
          const raw = json?.data?.data || json?.data?.agencies || json?.data || [];
          myAgencies = Array.isArray(raw) ? raw : [];
        }

        // 2. Also fetch sub-agencies (for agencies where user is owner/admin)
        let subAgencies: any[] = [];
        try {
          const subRes = await fetch(getApiUrl("agencies/sub-agencies/fetch"), { headers });
          if (subRes.ok) {
            const json = await subRes.json();
            const raw = json?.data?.data || json?.data?.subAgencies || json?.data || [];
            subAgencies = Array.isArray(raw) ? raw : raw?.rows || [];
            if (!Array.isArray(subAgencies)) subAgencies = [];
          }
        } catch {}

        // 3. Merge and deduplicate — build a flat list of all accessible agencies with hierarchy info
        const agencyMap = new Map<string, { id: string; name: string; role: string; isCurrentAgency: boolean; parentAgencyId?: string; logoUrl?: string }>();

        // Add current agency first
        if (currentAgencyObj?.id) {
          agencyMap.set(currentAgencyObj.id, {
            id: currentAgencyObj.id,
            name: currentAgencyObj.name || "My Agency",
            role: userRole,
            isCurrentAgency: true,
            parentAgencyId: currentAgencyObj.parentAgencyId || undefined,
            logoUrl: currentAgencyObj.logoUrl || undefined,
          });
        }

        // Add all agencies user is a direct member of
        for (const a of myAgencies) {
          const aId = a.id || a.agencyId || a.agency?.id;
          const aName = a.name || a.agencyName || a.agency?.name || "Agency";
          const aRole = (a.role || a.agencyRole || "agent").toLowerCase();
          const parentId = a.parentAgencyId || a.agency?.parentAgencyId;
          const logoUrl = a.logoUrl || a.logo_url || a.agency?.logoUrl || a.agency?.logo_url;
          if (aId && !agencyMap.has(aId)) {
            agencyMap.set(aId, { id: aId, name: aName, role: aRole, isCurrentAgency: aId === currentAgencyObj?.id, parentAgencyId: parentId || undefined, logoUrl: logoUrl || undefined });
          }
        }

        // Add sub-agencies with parent info
        for (const a of subAgencies) {
          const aId = a.id;
          const aName = a.name || "Sub-Agency";
          const logoUrl = a.logoUrl || a.logo_url;
          if (aId && !agencyMap.has(aId)) {
            agencyMap.set(aId, { id: aId, name: aName, role: "sub", isCurrentAgency: false, parentAgencyId: a.parentAgencyId || undefined, logoUrl: logoUrl || undefined });
          }
        }

        return Array.from(agencyMap.values());
      } catch {
        return [];
      }
    },
    enabled: !!userId,
    staleTime: 120000,
  });

  // Build agency list for the switcher dropdown
  // Fetch per-agency member + online counts for the org chart
  const { data: agencyStats = {} } = useQuery<Record<string, { memberCount: number; onlineCount: number }>>({
    queryKey: ["team-chat", "agency-stats"],
    queryFn: async () => {
      try {
        const { getSupabaseSession } = await import("@/app/_module/services/auth");
        const { getApiUrl } = await import("@/app/_module/services/config");
        const AgencyTokenMgr = (await import("@/app/_module/services/agencyTokenManager")).default;
        const session = await getSupabaseSession();
        const agId = AgencyTokenMgr.getCurrentAgencyId();
        const res = await fetch(getApiUrl("v1/team-chat/agency-stats"), {
          headers: { Authorization: `Bearer ${session?.access_token}`, ...(agId ? { 'x-agency-id': agId } : {}) },
        });
        if (!res.ok) return {};
        const json = await res.json();
        // Handle potential double-wrapping: { statusCode, data: { data: {...} } } or { success, data: {...} }
        const raw = json?.data?.data || json?.data || json || {};
        return raw;
      } catch { return {}; }
    },
    enabled: !!userId && allUserAgencies.length > 0,
    staleTime: 60000,
  });

  // Fetch network members for DM sidebar (owner only — all sub-agency members grouped by agency)
  const { data: networkMembers = [] } = useQuery<any[]>({
    queryKey: ["team-chat", "network-members"],
    queryFn: async () => {
      try {
        const { getSupabaseSession } = await import("@/app/_module/services/auth");
        const { getApiUrl } = await import("@/app/_module/services/config");
        const AgencyTokenMgr = (await import("@/app/_module/services/agencyTokenManager")).default;
        const session = await getSupabaseSession();
        const agId = AgencyTokenMgr.getCurrentAgencyId();
        const res = await fetch(getApiUrl("v1/team-chat/network-members"), {
          headers: { Authorization: `Bearer ${session?.access_token}`, ...(agId ? { 'x-agency-id': agId } : {}) },
        });
        if (!res.ok) return [];
        const json = await res.json();
        return json?.data?.data || json?.data || [];
      } catch { return []; }
    },
    enabled: !!userId && isOwnerOrAdmin,
    staleTime: 120000,
  });

  const agencyList = allUserAgencies.map(a => ({
    id: a.id,
    name: a.name,
    isCurrent: a.isCurrentAgency,
    role: a.role,
    parentAgencyId: a.parentAgencyId,
    logoUrl: a.logoUrl || (agencyStats[a.id] as any)?.logoUrl,
    memberCount: agencyStats[a.id]?.memberCount,
    onlineCount: agencyStats[a.id]?.onlineCount,
  }));

  // For announcement channels, check if user is owner/admin of the channel's owning agency
  // (not just their current agency — important for cross-agency announcement channels)
  const canPostInChannel = useMemo(() => {
    if (!isAnnouncementChannel) return true;
    if (!activeChannel) return false;
    const channelAgencyId = activeChannel.channel.agencyId;
    // 1. Instant check: AgencyTokenManager reads sessionStorage (set at login, always available)
    const storedAgency = AgencyTokenManager.getCurrentAgency();
    if (storedAgency && storedAgency.id === channelAgencyId && ['owner', 'admin'].includes((storedAgency.role || '').toLowerCase())) return true;
    // 2. Check agencyList (from API — covers cross-agency announcement channels)
    const agencyEntry = agencyList.find(a => a.id === channelAgencyId);
    if (agencyEntry && ['owner', 'admin'].includes(agencyEntry.role || '')) return true;
    // 3. Fallback: React context role for current agency
    if (channelAgencyId === currentAgencyObj?.id && isOwnerOrAdmin) return true;
    // Block by default for announcement channels — agents cannot post
    return false;
  }, [isAnnouncementChannel, activeChannel, currentAgencyObj?.id, isOwnerOrAdmin, agencyList]);

  // When switching agencies, override the x-agency-id header for team chat requests
  const effectiveChatAgencyId = chatAgencyId || currentAgencyObj?.id;

  // Resolve agency logo for the active chat agency (switches when you change agency)
  // If switched to a sub-agency with no logo, show nothing — don't fall back to parent's logo
  const activeAgency = agencyList.find(a => a.id === effectiveChatAgencyId);
  const isViewingOwnAgency = !chatAgencyId || chatAgencyId === currentAgencyObj?.id;
  const agencyLogoResolved = isViewingOwnAgency
    ? (activeAgency?.logoUrl || (branding as any)?.agencyProfileLogo || "")
    : (activeAgency?.logoUrl || "");

  // ── Mutations ────────────────────────────────────────────────────────────

  const sendMutation = useMutation({
    mutationFn: ({ channelId, content }: { channelId: string; content: string }) =>
      teamChatService.sendMessage(channelId, content),
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] });
        queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
      }, 500);
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ messageId, content }: { messageId: string; content: string }) =>
      teamChatService.editMessage(messageId, content),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (messageId: string) => teamChatService.deleteMessage(messageId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] }),
  });

  const reactMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      teamChatService.addReaction(messageId, emoji),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] }),
  });

  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: ({ channelId, file, content }: { channelId: string; file: File; content?: string }) =>
      teamChatService.uploadFile(channelId, file, content),
    onSuccess: () => {
      setUploadError(null);
      queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] });
      queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
    },
    onError: (err: any) => {
      const msg = err?.message?.includes('413') || err?.message?.includes('too large')
        ? 'File is too large. Max size is 15MB.'
        : err?.message?.includes('type not supported')
        ? 'File type not supported.'
        : 'Failed to upload file. Please try again.';
      setUploadError(msg);
      setTimeout(() => setUploadError(null), 5000);
    },
  });

  const sendReplyMutation = useMutation({
    mutationFn: ({ channelId, content, parentMessageId }: { channelId: string; content: string; parentMessageId: string }) =>
      teamChatService.sendReply(channelId, content, parentMessageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] });
      queryClient.invalidateQueries({ queryKey: ["team-chat", "thread", threadParentId] });
      queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
    },
  });

  const createChannelMutation = useMutation({
    mutationFn: ({ name, description }: { name: string; description?: string }) =>
      teamChatService.createChannel(name, description),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
      setActiveChannelId(data.id);
      setShowCreateChannel(false);
      setNewChannelName("");
      setNewChannelDesc("");
    },
  });

  const startDmMutation = useMutation({
    mutationFn: ({ targetUserId }: { targetUserId: string }) =>
      teamChatService.getOrCreateDm([targetUserId]),
    onSuccess: async (data) => {
      if (data?.id) {
        // Unhide the DM if it was closed
        try {
          const stored = localStorage.getItem('team-chat-hidden-dms');
          if (stored) {
            const hidden: string[] = JSON.parse(stored);
            const filtered = hidden.filter(id => id !== data.id);
            if (filtered.length !== hidden.length) {
              localStorage.setItem('team-chat-hidden-dms', JSON.stringify(filtered));
            }
          }
        } catch {}
        // Refetch channels first so the new DM appears, then select it
        await queryClient.refetchQueries({ queryKey: ["team-chat", "channels"] });
        setActiveChannelId(data.id);
      }
    },
  });

  const handleStartDm = useCallback((targetUserId: string, _userName: string) => {
    startDmMutation.mutate({ targetUserId });
  }, [startDmMutation]);

  const groupDmMutation = useMutation({
    mutationFn: ({ userIds, groupName }: { userIds: string[]; groupName?: string }) =>
      teamChatService.getOrCreateDm(userIds, groupName),
    onSuccess: (data) => {
      if (data?.id) {
        try {
          const stored = localStorage.getItem('team-chat-hidden-dms');
          if (stored) {
            const hidden: string[] = JSON.parse(stored);
            const filtered = hidden.filter(id => id !== data.id);
            if (filtered.length !== hidden.length) {
              localStorage.setItem('team-chat-hidden-dms', JSON.stringify(filtered));
            }
          }
        } catch {}
        setActiveChannelId(data.id);
        queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
      }
    },
  });

  const handleCreateGroupDm = useCallback((userIds: string[], groupName?: string) => {
    groupDmMutation.mutate({ userIds, groupName });
  }, [groupDmMutation]);

  // ── Effects ──────────────────────────────────────────────────────────────

  // Sync agency override when switching agencies — clear everything first, then refetch
  useEffect(() => {
    // Set the override FIRST so all subsequent fetches use the new agency
    setTeamChatAgencyOverride(chatAgencyId);

    // Clear all cached data immediately (not just invalidate — REMOVE old data)
    queryClient.removeQueries({ queryKey: ["team-chat", "channels"] });
    queryClient.removeQueries({ queryKey: ["team-chat", "messages"] });
    queryClient.removeQueries({ queryKey: ["team-chat", "members"] });
    queryClient.removeQueries({ queryKey: ["team-chat", "all-members"] });
    queryClient.removeQueries({ queryKey: ["team-chat", "pinned"] });
    queryClient.removeQueries({ queryKey: ["team-chat", "read-receipts"] });
    queryClient.removeQueries({ queryKey: ["team-chat", "thread"] });

    // Reset all UI state
    setActiveChannelId(null);
    setThreadParentId(null);
    setThreadInput("");
    setReplyingTo(null);
    setShowSearch(false);
    setSearchQuery("");
    setSearchResults([]);
    setShowPinnedBar(false);
    setEphemeralCard(null);
    setTypingUsers([]);

    // Immediately refetch channels for the new agency
    queryClient.refetchQueries({ queryKey: ["team-chat", "channels"] });
  }, [chatAgencyId, queryClient]);

  // Auto-select channel — from URL param or default to General
  useEffect(() => {
    if (channels.length > 0) {
      // If no channel selected, or selected channel doesn't exist in current list (stale from agency switch)
      const channelExists = activeChannelId && channels.some(c => c.channel.id === activeChannelId);
      if (!channelExists) {
        // Check URL for ?channel=id (from notification click)
        const urlParams = new URLSearchParams(window.location.search);
        const channelFromUrl = urlParams.get('channel');
        if (channelFromUrl && channels.some(c => c.channel.id === channelFromUrl)) {
          setActiveChannelId(channelFromUrl);
          window.history.replaceState({}, '', '/team-chat');
          return;
        }
        const general = channels.find(c => c.channel.slug === "general");
        setActiveChannelId(general?.channel.id || channels[0].channel.id);
      }
    }
  }, [channels, activeChannelId]);

  // Save/restore draft messages per channel
  const prevChannelRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevChannelRef.current && messageInput.trim()) {
      sessionStorage.setItem('team-chat-draft-' + prevChannelRef.current, messageInput);
    } else if (prevChannelRef.current) {
      sessionStorage.removeItem('team-chat-draft-' + prevChannelRef.current);
    }
    if (activeChannelId) {
      setMessageInput(sessionStorage.getItem('team-chat-draft-' + activeChannelId) || "");
    }
    prevChannelRef.current = activeChannelId;
  }, [activeChannelId]);

  // Auto-scroll is now handled by the VirtualizedMessageList component

  // Mark channel as read when viewing — then refresh channel list to clear badge
  useEffect(() => {
    if (activeChannelId) {
      teamChatService.markRead(activeChannelId).then(() => {
        // Refresh channels to update unread count (clears the red badge)
        queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
      }).catch(() => {});
    }
  }, [activeChannelId, messages.length, queryClient]);

  // ── Supabase Realtime — instant message delivery ─────────────────────────
  useEffect(() => {
    if (!activeChannelId) return;

    let channel: any;
    (async () => {
      try {
        const { getSupabaseClient } = await import("@/app/_module/services/supabaseClient");
        const supabase = await getSupabaseClient();

        channel = supabase
          .channel(`team-chat-${activeChannelId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'team_chat_messages',
              filter: `channel_id=eq.${activeChannelId}`,
            },
            () => {
              queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] });
              queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
            }
          )
          // UPDATE too: a #sales-wins card's amount/carrier is rewritten in place
          // when the policy is edited or re-entered, and a soft delete (deleted_at
          // set when the last sold policy is removed) arrives as an UPDATE, not a
          // DELETE. Without this an open chat kept showing the stale card until
          // the channel was reopened. The refetch already excludes deleted rows.
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'team_chat_messages',
              filter: `channel_id=eq.${activeChannelId}`,
            },
            () => {
              queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] });
            }
          )
          .subscribe();
      } catch (err) {
        console.warn('Team chat realtime subscription failed, falling back to polling', err);
      }
    })();

    return () => {
      if (channel) {
        removeRealtimeChannel(channel);
      }
    };
  }, [activeChannelId, queryClient]);

  // (Removed the Chrome desktop-notification permission prompt — team chat no
  // longer nags users to enable browser notifications. In-app sound + unread
  // badges still work; existing already-granted users keep desktop pop-ups.)

  // ── Supabase Realtime — global listener for unread badges across ALL channels ──
  useEffect(() => {
    let globalChannel: any;
    (async () => {
      try {
        const { getSupabaseClient } = await import("@/app/_module/services/supabaseClient");
        const supabase = await getSupabaseClient();

        globalChannel = supabase
          .channel('team-chat-global')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'team_chat_messages',
            },
            (payload: any) => {
              const msgChannelId = payload?.new?.channel_id;
              const senderId = payload?.new?.sender_id;
              if (msgChannelId && msgChannelId !== activeChannelId) {
                queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
              }

              // Check if the channel is muted (used for both sound and desktop notification)
              const ch = channels.find(c => c.channel.id === msgChannelId);
              const isMuted = ch ? members.some(m => m.userId === userId && m.isMuted) : false;

              // Play notification sound for messages from others in different channels or when tab is hidden
              if (
                senderId &&
                senderId !== userId &&
                !isMuted &&
                (msgChannelId !== activeChannelId || (typeof document !== 'undefined' && document.hidden))
              ) {
                playNotificationSound();
              }

              // Desktop push notification for messages from others when tab is not focused
              if (
                senderId &&
                senderId !== userId &&
                typeof document !== 'undefined' &&
                document.hidden &&
                typeof window !== 'undefined' &&
                'Notification' in window &&
                Notification.permission === 'granted'
              ) {
                if (!isMuted && ch) {
                  const channelName = ch.channel.type === 'dm' ? 'Direct Message' : ch.channel.name;
                  const messagePreview = payload?.new?.content?.slice(0, 100) || 'New message';
                  const notification = new Notification(`New message in #${channelName}`, {
                    body: messagePreview,
                    icon: '/favicon.ico',
                    tag: `team-chat-${msgChannelId}`,
                  });
                  notification.onclick = () => {
                    window.focus();
                    setActiveChannelId(msgChannelId);
                    notification.close();
                  };
                }
              }
            }
          )
          .subscribe();
      } catch {
        // Silent fail — polling is the fallback
      }
    })();

    return () => {
      if (globalChannel) {
        removeRealtimeChannel(globalChannel);
      }
    };
  }, [activeChannelId, queryClient, userId, channels, members, playNotificationSound]);

  // ── Socket.io — Typing Indicators ──────────────────────────────────────
  useEffect(() => {
    if (!activeChannelId) return;

    let socket: any;
    let mounted = true;

    (async () => {
      try {
        const { io } = await import("socket.io-client");
        const { getBackendUrl } = await import("@/app/_module/services/config");
        const { getSupabaseSession } = await import("@/app/_module/services/auth");
        const session = await getSupabaseSession();
        const baseUrl = getBackendUrl("").replace(/\/+$/, "");
        socket = io(`${baseUrl}/team-chat`, {
          transports: ["websocket"],
          reconnection: true,
          reconnectionDelay: 2000,
          auth: { token: session?.access_token },
        });
        if (!mounted) { socket.disconnect(); return; }
        socketRef.current = socket;

        socket.emit("channel:join", { channelId: activeChannelId });

        const typingTimers = new Map<string, NodeJS.Timeout>();
        socket.on("typing", (data: { channelId: string; userName: string; isTyping: boolean }) => {
          if (!mounted || data.channelId !== activeChannelId) return;
          const existing = typingTimers.get(data.userName);
          if (existing) clearTimeout(existing);

          if (data.isTyping) {
            setTypingUsers(prev => prev.includes(data.userName) ? prev : [...prev, data.userName]);
            typingTimers.set(data.userName, setTimeout(() => {
              if (!mounted) return;
              setTypingUsers(prev => prev.filter(n => n !== data.userName));
              typingTimers.delete(data.userName);
            }, 5000));
          } else {
            setTypingUsers(prev => prev.filter(n => n !== data.userName));
            typingTimers.delete(data.userName);
          }
        });
      } catch (err) {
        console.warn("Team chat socket connection failed:", err);
      }
    })();

    return () => {
      mounted = false;
      setTypingUsers([]);
      if (socket) {
        socket.emit("channel:leave", { channelId: activeChannelId });
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [activeChannelId]);

  // Emit typing indicator (debounced)
  const emitTyping = useCallback((isTyping: boolean) => {
    const socket = socketRef.current;
    if (!socket || !activeChannelId) return;
    const userName = (user as any)?.user?.name || (user as any)?.name || "Someone";
    const now = Date.now();
    if (isTyping && now - lastTypingEmitRef.current < 2000) return;
    lastTypingEmitRef.current = now;
    socket.emit("typing", { channelId: activeChannelId, userName, isTyping });
  }, [activeChannelId, user]);

  // ── Supabase Presence — Real-time Online Status ────────────────────────
  const presenceChannelRef = useRef<any>(null);
  const currentUserStatus = (user as any)?.user?.userStatus || (user as any)?.user?.user_status || "available";

  useEffect(() => {
    let presenceChannel: any;

    (async () => {
      try {
        const { getSupabaseClient } = await import("@/app/_module/services/supabaseClient");
        const AgencyTokenMgr = (await import("@/app/_module/services/agencyTokenManager")).default;
        const supabase = await getSupabaseClient();
        const agencyId = AgencyTokenMgr.getCurrentAgencyId();
        if (!agencyId || !userId) return;

        const userName = (user as any)?.user?.name || (user as any)?.name || "Agent";

        presenceChannel = supabase.channel(`team-chat-presence:${agencyId}`, {
          config: { presence: { key: userId } },
        });
        presenceChannelRef.current = presenceChannel;

        presenceChannel
          .on('presence', { event: 'sync' }, () => {
            const state = presenceChannel.presenceState();
            const newMap: Record<string, { status: string; lastSeen?: string }> = {};
            for (const [key, presences] of Object.entries(state)) {
              const p = (presences as any[])?.[0];
              if (p) {
                newMap[key] = {
                  status: p.status || "online",
                  lastSeen: p.lastSeen,
                };
              }
            }
            setPresenceMap(newMap);
          })
          .on('presence', { event: 'leave' }, ({ key }: { key: string }) => {
            setPresenceMap(prev => ({
              ...prev,
              [key]: { status: "offline", lastSeen: new Date().toISOString() },
            }));
          })
          .subscribe(async (status: string) => {
            if (status === 'SUBSCRIBED') {
              await presenceChannel.track({
                status: currentUserStatus === "on_call" ? "on-call" : currentUserStatus,
                name: userName,
                lastSeen: new Date().toISOString(),
              });
            }
          });
      } catch (err) {
        console.warn("Presence subscription failed:", err);
      }
    })();

    return () => {
      if (presenceChannel) {
        removeRealtimeChannel(presenceChannel);
      }
      presenceChannelRef.current = null;
    };
  }, [userId, user]);

  // Re-track presence when user status changes
  useEffect(() => {
    const ch = presenceChannelRef.current;
    if (!ch || !userId) return;
    const userName = (user as any)?.user?.name || (user as any)?.name || "Agent";
    ch.track({
      status: currentUserStatus === "on_call" ? "on-call" : currentUserStatus,
      name: userName,
      lastSeen: new Date().toISOString(),
    }).catch(() => {});
  }, [currentUserStatus, userId, user]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isCmd = e.metaKey || e.ctrlKey;

      // Ctrl/Cmd + K — Focus channel search in sidebar
      if (isCmd && e.key === 'k') {
        e.preventDefault();
        sidebarSearchRef.current?.focus();
        return;
      }

      // Escape — Close panels in priority order
      if (e.key === 'Escape') {
        // Don't intercept if user is typing in an input/textarea (except for closing panels)
        if (threadParentId) { setThreadParentId(null); setThreadInput(""); return; }
        if (showSearch) { setShowSearch(false); setSearchQuery(""); setSearchResults([]); return; }
        if (showPinnedBar) { setShowPinnedBar(false); return; }
        if (ephemeralCard) { setEphemeralCard(null); return; }
      }

      // Ctrl/Cmd + Shift + M — Toggle mute on current channel
      if (isCmd && e.shiftKey && (e.key === 'M' || e.key === 'm')) {
        e.preventDefault();
        if (!activeChannelId) return;
        teamChatService.toggleMute(activeChannelId).then(() => {
          setChannelMuted(prev => !prev);
          queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
          queryClient.invalidateQueries({ queryKey: ["team-chat", "members", activeChannelId] });
        }).catch(() => {});
        return;
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [threadParentId, showSearch, showPinnedBar, ephemeralCard, activeChannelId, queryClient]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  // Slash commands
  const slashCommands = [
    { cmd: "/leaderboard", desc: "Post team leaderboard to chat", emoji: "\uD83C\uDFC6" },
    { cmd: "/mystats", desc: "Your stats (only you see this)", emoji: "\uD83D\uDCCA" },
    { cmd: "/premium", desc: "Post premium report to chat", emoji: "\uD83D\uDCB0" },
    { cmd: "/pipeline", desc: "Your pipeline (only you see this)", emoji: "\uD83D\uDCCB" },
    { cmd: "/help", desc: "Show available commands", emoji: "\u2753" },
  ];

  const showSlashMenu = messageInput === "/" || (messageInput.startsWith("/") && messageInput.length < 15 && !messageInput.includes(" "));
  const filteredCommands = messageInput.length > 1
    ? slashCommands.filter(c => c.cmd.startsWith(messageInput.toLowerCase()))
    : slashCommands;

  const executeSlashCommand = useCallback(async (cmd: string) => {
    if (!activeChannelId) return;
    setMessageInput("");

    if (cmd === "/help") {
      const helpText = slashCommands.map(c => `${c.emoji} ${c.cmd} — ${c.desc}`).join("\n");
      setEphemeralCard(helpText);
      return;
    }

    try {
      const { getApiUrl } = await import("@/app/_module/services/config");
      const { getSupabaseSession } = await import("@/app/_module/services/auth");
      const AgencyTokenMgr = (await import("@/app/_module/services/agencyTokenManager")).default;
      const session = await getSupabaseSession();
      const agencyId = AgencyTokenMgr.getCurrentAgencyId();
      const headers: any = {
        Authorization: `Bearer ${session?.access_token}`,
        "Content-Type": "application/json",
        ...(agencyId ? { "x-agency-id": agencyId } : {}),
      };

      if (cmd === "/leaderboard" || cmd === "/mystats" || cmd === "/premium" || cmd === "/pipeline") {
        // For /mystats and /pipeline we need visibleOnly=false so the current user is always included
        const isPersonalCmd = cmd === "/mystats" || cmd === "/pipeline";
        const params = new URLSearchParams({
          period: "this-month",
          primaryMetric: "totalSales",
          visibleOnly: isPersonalCmd ? "false" : "true",
        });
        const res = await fetch(getApiUrl(`leaderboard/stats?${params.toString()}`), { headers });
        if (res.ok) {
          const json = await res.json();
          // Backend returns { statusCode, message, data: { stats, leaderboard }, timestamp }
          const responseData = json?.data || {};
          const agents: any[] = responseData?.leaderboard || [];
          const summary: any = responseData?.stats || {};

          if (cmd === "/leaderboard") {
            const top = agents.slice(0, 10).map((a: any) => ({
              name: a.name || 'Agent', sales: a.totalSales || 0, calls: a.callsMade || 0,
              premium: a.totalPremium || 0, rank: a.rank || 0, avatar: a.profileImageUrl || a.profile_image_url,
            }));
            sendMutation.mutate({ channelId: activeChannelId, content: `::cmd::${JSON.stringify({ type: "leaderboard", agents: top, period: "This Month" })}` });
          } else if (cmd === "/mystats") {
            const me = agents.find((a: any) => a.userId === userId || a.id === userId || a.agentId === userId);
            const policies = me?.totalPolicies || me?.totalSales || 0;
            const leads = me?.totalLeads || 0;
            const appts = me?.appointments || 0;
            const convRate = leads > 0 ? ((appts / leads) * 100) : 0;
            const closeRate = appts > 0 ? Math.min(((policies / appts) * 100), 100) : (policies > 0 ? 100 : 0);
            const myPremium = me?.totalPremium || me?.premium || 0;
            const avgPremium = policies > 0 ? Math.round(myPremium / policies) : 0;
            setEphemeralCard(`::cmd::${JSON.stringify({
              type: "mystats", name: me?.name || "You", rank: me?.rank || 0,
              policies,
              premium: myPremium,
              calls: me?.callsMade || 0,
              appointments: appts,
              leads,
              convRate,
              closeRate,
              avgPremium,
              period: "This Month",
            })}`);
          } else if (cmd === "/premium") {
            sendMutation.mutate({ channelId: activeChannelId, content: `::cmd::${JSON.stringify({
              type: "premium", total: summary?.totalPremium || 0, sales: summary?.totalPolicies || summary?.totalSales || 0,
              period: "This Month",
            })}` });
          } else if (cmd === "/pipeline") {
            const me = agents.find((a: any) => a.userId === userId || a.id === userId || a.agentId === userId);
            setEphemeralCard(`::cmd::${JSON.stringify({
              type: "pipeline",
              leads: me?.totalLeads || 0,
              appointments: me?.appointments || 0,
              calls: me?.callsMade || 0,
              sold: me?.totalPolicies || me?.totalSales || 0,
              premium: me?.totalPremium || me?.premium || 0,
              period: "This Month",
            })}`);
          }
        } else {
          sendMutation.mutate({ channelId: activeChannelId, content: `Warning: Could not load stats (${res.status}). Try again.` });
        }
      }
    } catch {
      sendMutation.mutate({ channelId: activeChannelId, content: `Warning: Command failed. Try again.` });
    }
  }, [activeChannelId, sendMutation, userId]);

  // Convert @Name mentions to <@userId:Name> or <@agency:agencyId:AgencyName> format before sending
  const convertMentionsForSend = useCallback((text: string): string => {
    let result = text;
    for (const [displayName, refId] of mentionedUsersRef.current.entries()) {
      // @everyone stays as plain text — backend parses it directly
      if (refId === 'everyone') continue;
      const escapedName = displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (refId.startsWith('agency:')) {
        const agencyId = refId.replace('agency:', '');
        result = result.replace(new RegExp(`@${escapedName}(?=\\s|$)`, 'g'), `<@agency:${agencyId}:${displayName}>`);
      } else {
        result = result.replace(new RegExp(`@${escapedName}(?=\\s|$)`, 'g'), `<@${refId}:${displayName}>`);
      }
    }
    return result;
  }, []);

  const lastSendTimeRef = useRef(0);
  const handleSend = useCallback(() => {
    // DEMO SANDBOX: offer the trial instead of posting.
    //
    // Team chat is the one seeded surface that is genuinely SHARED — every seat's agency
    // has the same channels, and chat messages are not in the reset's table list. A
    // message posted by one visitor would sit in the channel for everyone who came after,
    // and unlike a stray renamed lead it is prominent and permanent.
    //
    // Reading the conversation is the demo; contributing to it is the product.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Sending messages is disabled in the demo sandbox. Start a free trial to chat with your team.',
        }),
      );
      return;
    }

    // Rate limit: 500ms cooldown between messages
    const now = Date.now();
    if (now - lastSendTimeRef.current < 500) return;
    lastSendTimeRef.current = now;

    // If there's a pending file, upload it (with optional message)
    if (pendingFile && activeChannelId) {
      uploadMutation.mutateAsync({ channelId: activeChannelId, file: pendingFile, content: messageInput.trim() || undefined });
      setPendingFile(null);
      if (pendingFilePreview) { URL.revokeObjectURL(pendingFilePreview); setPendingFilePreview(null); }
      setMessageInput("");
      if (activeChannelId) sessionStorage.removeItem('team-chat-draft-' + activeChannelId);
      inputRef.current?.focus();
      return;
    }

    if (!messageInput.trim() || !activeChannelId) return;

    const trimmed = messageInput.trim();
    if (trimmed.startsWith("/")) {
      const matchedCmd = slashCommands.find(c => c.cmd === trimmed.toLowerCase());
      if (matchedCmd) {
        executeSlashCommand(matchedCmd.cmd);
        return;
      }
    }

    const contentToSend = convertMentionsForSend(trimmed);

    // If replying to a message, send as a reply
    if (replyingTo) {
      sendReplyMutation.mutate({ channelId: activeChannelId, content: contentToSend, parentMessageId: replyingTo.id });
      setReplyingTo(null);
    } else {
      sendMutation.mutate({ channelId: activeChannelId, content: contentToSend });
    }
    setMessageInput("");
    if (activeChannelId) sessionStorage.removeItem('team-chat-draft-' + activeChannelId);
    setMentionSearch(null);
    mentionedUsersRef.current.clear();
    emitTyping(false);
    inputRef.current?.focus();
  }, [messageInput, activeChannelId, sendMutation, sendReplyMutation, replyingTo, executeSlashCommand, emitTyping, convertMentionsForSend, pendingFile, pendingFilePreview, uploadMutation]);

  const handleThreadSend = useCallback(() => {
    if (!threadInput.trim() || !activeChannelId || !threadParentId) return;
    sendReplyMutation.mutate({ channelId: activeChannelId, content: threadInput.trim(), parentMessageId: threadParentId });
    setThreadInput("");
  }, [threadInput, activeChannelId, threadParentId, sendReplyMutation]);

  // Handle @mention detection on input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessageInput(value);

    emitTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => emitTyping(false), 3000);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      setMentionSearch({ query: mentionMatch[1], startIndex: mentionMatch.index! });
      setMentionSelectedIndex(0);
    } else {
      setMentionSearch(null);
    }
  }, [emitTyping]);

  // Insert mention into input
  const insertMention = useCallback((member: ChannelMember) => {
    if (!mentionSearch) return;
    const displayName = member.user.name || member.user.email;
    const before = messageInput.substring(0, mentionSearch.startIndex);
    const after = messageInput.substring(mentionSearch.startIndex + mentionSearch.query.length + 1);
    const mention = `@${displayName} `;
    setMessageInput(before + mention + after);
    mentionedUsersRef.current.set(displayName, member.userId);
    setMentionSearch(null);
    setMentionSelectedIndex(0);
    inputRef.current?.focus();
  }, [mentionSearch, messageInput]);

  // Insert agency group mention into input (for announcement channels)
  // Format: <@agency:agencyId:AgencyName> — backend needs to parse this for notifications
  const insertAgencyMention = useCallback((agency: { id: string; name: string }) => {
    if (!mentionSearch) return;
    const before = messageInput.substring(0, mentionSearch.startIndex);
    const after = messageInput.substring(mentionSearch.startIndex + mentionSearch.query.length + 1);
    const mention = `@${agency.name} `;
    setMessageInput(before + mention + after);
    // Store with a special prefix so convertMentionsForSend knows it's an agency mention
    mentionedUsersRef.current.set(agency.name, `agency:${agency.id}`);
    setMentionSearch(null);
    setMentionSelectedIndex(0);
    inputRef.current?.focus();
  }, [mentionSearch, messageInput]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle @mention navigation (agency groups + individual members)
    if (mentionSearch) {
      const filteredMembers = members.filter(m =>
        (m.user.name || m.user.email || "").toLowerCase().includes(mentionSearch.query.toLowerCase())
      ).slice(0, 8);

      // Special mentions (@everyone) — only for owners/admins
      const mq = mentionSearch.query.toLowerCase();
      const specialMentions = isOwnerOrAdmin ? [
        { id: "everyone", name: "everyone" },
      ].filter(s => !mq || s.name.includes(mq)) : [];

      // Build agency options for announcement channels
      const agencyOptions: Array<{ id: string; name: string }> = [];
      if (isAnnouncementChannel && agencyList.length > 0) {
        for (const agency of agencyList) {
          if (!mq || agency.name.toLowerCase().includes(mq)) {
            agencyOptions.push({ id: agency.id, name: agency.name });
          }
        }
      }
      const totalItems = specialMentions.length + agencyOptions.length + filteredMembers.length;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionSelectedIndex(prev => Math.min(prev + 1, totalItems - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionSelectedIndex(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        if (mentionSelectedIndex < specialMentions.length) {
          // Selected @everyone — insert as a fake member so insertMention handles it
          const s = specialMentions[mentionSelectedIndex];
          insertMention({ userId: s.id, user: { name: s.name, email: "", id: s.id } } as any);
        } else if (mentionSelectedIndex < specialMentions.length + agencyOptions.length) {
          // Selected an agency group
          insertAgencyMention(agencyOptions[mentionSelectedIndex - specialMentions.length]);
        } else if (filteredMembers[mentionSelectedIndex - specialMentions.length - agencyOptions.length]) {
          // Selected an individual member
          insertMention(filteredMembers[mentionSelectedIndex - specialMentions.length - agencyOptions.length]);
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionSearch(null);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "Escape" && ephemeralCard) {
      setEphemeralCard(null);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <div
        className="team-chat-root fixed right-[60px] bottom-0 flex overflow-hidden bg-white dark:bg-zinc-950 z-[5] transition-[left] duration-200 ease-linear"
        style={{ left: 'var(--sidebar-width, 14rem)', top: '64px' }}
        ref={(el) => {
          if (!el) return;
          const observer = new MutationObserver(() => {
            const wrapper = document.querySelector('[data-slot="sidebar-wrapper"]');
            const sidebar = wrapper?.querySelector('[data-slot="sidebar"]');
            if (sidebar) {
              const state = sidebar.getAttribute('data-state');
              el.style.left = state === 'collapsed' ? 'var(--sidebar-width-icon, 3rem)' : 'var(--sidebar-width, 14rem)';
            }
          });
          const sidebar = document.querySelector('[data-slot="sidebar"]');
          if (sidebar) {
            observer.observe(sidebar, { attributes: true, attributeFilter: ['data-state'] });
            const state = sidebar.getAttribute('data-state');
            el.style.left = state === 'collapsed' ? 'var(--sidebar-width-icon, 3rem)' : 'var(--sidebar-width, 14rem)';
          }
        }}
      >
        {/* Loading overlay — covers entire team chat container including sidebar */}
        {channelsLoading && <TeamChatSkeleton />}

        {/* Mobile sidebar trigger */}
        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent side="left" className="team-chat-drawer w-64 p-0">
            <ChannelSidebar
              channels={channels}
              activeChannelId={activeChannelId}
              onSelect={id => { setActiveChannelId(id); setMobileSidebarOpen(false); }}
              onCreateChannel={(name) => createChannelMutation.mutate({ name })}
              onEditChannel={isOwnerOrAdmin ? async (channelId, newName) => {
                try {
                  await teamChatService.renameChannel(channelId, newName);
                  queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
                } catch {}
              } : undefined}
              onDeleteChannel={isOwnerOrAdmin ? async (channelId) => {
                try {
                  await teamChatService.deleteChannel(channelId);
                  if (activeChannelId === channelId) setActiveChannelId(null);
                  queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
                } catch {}
              } : undefined}
              onReorderChannel={isOwnerOrAdmin ? async (channelId, direction) => {
                try {
                  await teamChatService.reorderChannel(channelId, direction);
                  queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
                } catch {}
              } : undefined}
              onToggleAnnouncement={isOwnerOrAdmin ? async (channelId) => {
                try {
                  await teamChatService.toggleAnnouncement(channelId);
                  queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
                } catch {}
              } : undefined}
              onStartDm={handleStartDm}
              onCreateGroupDm={handleCreateGroupDm}
              agencyName={agencyName}
              agencyLogo={agencyLogoResolved}
              allMembers={allAgencyMembers}
              currentUserId={userId}
              currentUserAgencyRole={userRole}
              currentAgencyId={currentAgencyObj?.id}
              agencyList={agencyList}
              chatAgencyId={chatAgencyId}
              onSwitchAgency={(id) => { setChatAgencyId(id); setActiveChannelId(null); }}
              searchInputRef={sidebarSearchRef}
              networkMembers={isOwnerOrAdmin ? networkMembers : undefined}
              isOwnerOrAdmin={isOwnerOrAdmin}
            />
          </SheetContent>
        </Sheet>

        {/* Desktop sidebar */}
        <div className="hidden md:flex md:flex-col w-56 flex-shrink-0 h-full min-h-0 overflow-hidden">
          <ChannelSidebar
            channels={channels}
            activeChannelId={activeChannelId}
            onSelect={setActiveChannelId}
            onCreateChannel={(name) => createChannelMutation.mutate({ name })}
            onEditChannel={isOwnerOrAdmin ? async (channelId, newName) => {
              try {
                await teamChatService.renameChannel(channelId, newName);
                queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
              } catch {}
            } : undefined}
            onDeleteChannel={isOwnerOrAdmin ? async (channelId) => {
              try {
                await teamChatService.deleteChannel(channelId);
                if (activeChannelId === channelId) setActiveChannelId(null);
                queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
              } catch {}
            } : undefined}
            onReorderChannel={isOwnerOrAdmin ? async (channelId, direction) => {
              try {
                await teamChatService.reorderChannel(channelId, direction);
                queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
              } catch {}
            } : undefined}
            onToggleAnnouncement={isOwnerOrAdmin ? async (channelId) => {
              try {
                await teamChatService.toggleAnnouncement(channelId);
                queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
              } catch {}
            } : undefined}
            onStartDm={handleStartDm}
            onCreateGroupDm={handleCreateGroupDm}
            agencyName={agencyName}
            agencyLogo={agencyLogoResolved}
            allMembers={allAgencyMembers.length > 0 ? allAgencyMembers : members}
            currentUserId={userId}
            agencyList={agencyList}
            chatAgencyId={chatAgencyId}
            onSwitchAgency={(id) => { setChatAgencyId(id); setActiveChannelId(null); }}
            searchInputRef={sidebarSearchRef}
            networkMembers={isOwnerOrAdmin ? networkMembers : undefined}
            isOwnerOrAdmin={isOwnerOrAdmin}
          />
        </div>

        {/* Center — Messages */}
        <div role="main" aria-label="Chat messages" className="flex-1 flex flex-col min-w-0">
          {/* Channel header */}
          <div className="flex items-center gap-3 px-4 h-12 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex-shrink-0">
            <button onClick={() => setMobileSidebarOpen(true)} className="md:hidden p-1">
              <MessageSquare className="w-5 h-5 text-zinc-500" />
            </button>
            {activeChannel && (
              <>
                {activeChannel.channel.type === "dm" ? (
                  <MessageSquare className="w-4 h-4 text-blue-500" />
                ) : isAnnouncementChannel ? (
                  <Megaphone className="w-4 h-4 text-amber-500" />
                ) : activeChannel.channel.slug === "sales-wins" ? (
                  <Trophy className="w-4 h-4 text-yellow-500" />
                ) : (
                  <MessageSquare className="w-4 h-4 text-zinc-400" />
                )}
                <span className={`font-semibold text-sm ${isAnnouncementChannel ? 'text-amber-700 dark:text-amber-300' : 'text-zinc-800 dark:text-zinc-200'}`}>
                  {activeChannel.channel.type === "dm"
                    ? getDmName(activeChannel)
                    : activeChannel.channel.name}
                </span>
                {isChannelArchived && (
                  <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                    <Lock className="w-3 h-3" />
                    Archived
                  </span>
                )}
                {activeChannel.channel.type !== "dm" && activeChannel.channel.description && (
                  <span className="hidden sm:block text-xs text-zinc-400 dark:text-zinc-500 truncate">
                    — {(activeChannel.channel.description || '').replace(/^\[ARCHIVED\]\s*/, '')}
                  </span>
                )}
                {activeChannel.channel.type === "dm" && (
                  <span className="hidden sm:block text-xs text-emerald-500">
                    {getDmName(activeChannel) === 'Personal Notes' ? 'Personal Notes' : 'Direct Message'}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-1">
                  {/* Bookmarks button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowBookmarks(!showBookmarks)}
                    className="h-8 px-2 gap-1"
                    title={`${bookmarks.length} saved message${bookmarks.length !== 1 ? 's' : ''}`}
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${showBookmarks ? "text-amber-500" : "text-zinc-500"}`} />
                    {bookmarks.length > 0 && (
                      <span className="text-xs text-zinc-400">{bookmarks.length}</span>
                    )}
                  </Button>
                  {/* Archive button (owner/admin only, not for DMs) */}
                  {isOwnerOrAdmin && activeChannel.channel.type !== "dm" && !activeChannel.channel.isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (!activeChannelId) return;
                        try {
                          const res = await fetch(
                            (await import("@/app/_module/services/config")).getApiUrl(`team-chat/channels/${activeChannelId}/archive`),
                            {
                              method: 'PATCH',
                              headers: {
                                Authorization: `Bearer ${(await (await import("@/app/_module/services/auth")).getSupabaseSession())?.access_token}`,
                                'Content-Type': 'application/json',
                                ...((await import("@/app/_module/services/agencyTokenManager")).default.getCurrentAgencyId()
                                  ? { 'x-agency-id': (await import("@/app/_module/services/agencyTokenManager")).default.getCurrentAgencyId()! }
                                  : {}),
                              },
                            }
                          );
                          if (res.ok) {
                            queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
                            const data = await res.json();
                            setIsChannelArchived(data?.data?.isArchived ?? false);
                          }
                        } catch {}
                      }}
                      className="h-8 w-8 p-0"
                      title={isChannelArchived ? "Unarchive channel" : "Archive channel"}
                    >
                      <Archive className={`w-4 h-4 ${isChannelArchived ? "text-amber-500" : "text-zinc-500"}`} />
                    </Button>
                  )}
                  {/* Pinned messages button */}
                  {pinnedMessages.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPinnedBar(!showPinnedBar)}
                      className="h-8 px-2 gap-1"
                      title={`${pinnedMessages.length} pinned message${pinnedMessages.length !== 1 ? 's' : ''}`}
                    >
                      <Pin className={`w-3.5 h-3.5 ${showPinnedBar ? "text-blue-500" : "text-zinc-500"}`} />
                      <span className="text-xs text-zinc-400">{pinnedMessages.length}</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowSearch(!showSearch); if (showSearch) { setSearchQuery(""); setSearchResults([]); } }}
                    className="h-8 w-8 p-0"
                    title="Search messages"
                  >
                    <Search className={`w-4 h-4 ${showSearch ? "text-blue-500" : "text-zinc-500"}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (!activeChannelId) return;
                      try {
                        await teamChatService.toggleMute(activeChannelId);
                        setChannelMuted(!channelMuted);
                        queryClient.invalidateQueries({ queryKey: ["team-chat", "channels"] });
                        queryClient.invalidateQueries({ queryKey: ["team-chat", "members", activeChannelId] });
                      } catch {}
                    }}
                    className="h-8 w-8 p-0"
                    title={channelMuted ? "Unmute channel" : "Mute channel"}
                  >
                    {channelMuted ? (
                      <BellOff className="w-4 h-4 text-red-400" />
                    ) : (
                      <Bell className="w-4 h-4 text-zinc-500" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Below lg the members pane doesn't exist — open the
                      // Discord-style right drawer instead (phone/tablet).
                      if (typeof window !== "undefined" && window.innerWidth < 1024) {
                        setMobileMembersOpen(true);
                        return;
                      }
                      setShowMembers(!showMembers);
                    }}
                    className="h-8 w-8 p-0"
                    title={showMembers ? "Hide members" : "Show members"}
                  >
                    <Users className="w-4 h-4 text-zinc-500" />
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Pinned messages panel */}
          {showPinnedBar && pinnedMessages.length > 0 && (
            <div className="border-b border-zinc-200 dark:border-zinc-800 bg-amber-50/50 dark:bg-amber-950/10 max-h-60 overflow-y-auto">
              <div className="px-4 py-2 flex items-center gap-2 border-b border-amber-200/30 dark:border-amber-800/20">
                <Pin className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                  Pinned Messages ({pinnedMessages.length})
                </span>
                <button onClick={() => setShowPinnedBar(false)} className="ml-auto p-0.5 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30">
                  <X className="w-3.5 h-3.5 text-amber-500" />
                </button>
              </div>
              {pinnedMessages.map((pin: Message) => (
                <div key={pin.id} className="px-4 py-2.5 hover:bg-amber-100/30 dark:hover:bg-amber-900/10 transition-colors border-b border-amber-100/50 dark:border-amber-900/10 last:border-none cursor-pointer"
                  onClick={() => {
                    setShowPinnedBar(false);
                    setHighlightedMessageId(pin.id);
                    // Clear highlight after animation
                    setTimeout(() => setHighlightedMessageId(null), 2000);
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">{pin.sender?.name || "Unknown"}</span>
                    <span className="text-[10px] text-zinc-400">{new Date(pin.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                  </div>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2">
                    {(pin.content || "").replace(/<@[^>]+>/g, (m) => { const n = m.match(/:(.+)>/); return n ? `@${n[1]}` : '@someone'; }).replace(/::cmd::.*/g, '[Command]').replace(/::resource::.*/g, '[Resource]').replace(/::recording::.*/g, '[Recording]').substring(0, 150)}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Bookmarks panel */}
          {showBookmarks && (
            <div className="border-b border-zinc-200 dark:border-zinc-800 bg-blue-50/50 dark:bg-blue-950/10 max-h-60 overflow-y-auto">
              <div className="px-4 py-2 flex items-center gap-2 border-b border-blue-200/30 dark:border-blue-800/20">
                <Bookmark className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                  Saved Messages ({bookmarks.length})
                </span>
                <button onClick={() => setShowBookmarks(false)} className="ml-auto p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30">
                  <X className="w-3.5 h-3.5 text-blue-500" />
                </button>
              </div>
              {bookmarks.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-zinc-400">
                  No saved messages yet. Bookmark messages to find them here.
                </div>
              ) : (
                bookmarks.map(bm => (
                  <div
                    key={bm.id}
                    className="px-4 py-2.5 hover:bg-blue-100/30 dark:hover:bg-blue-900/10 transition-colors border-b border-blue-100/50 dark:border-blue-900/10 last:border-none cursor-pointer flex items-start gap-2"
                    onClick={() => {
                      // Navigate to the channel if different
                      if (bm.channelId !== activeChannelId) {
                        setActiveChannelId(bm.channelId);
                      }
                      setShowBookmarks(false);
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">{bm.senderName}</span>
                        <span className="text-[10px] text-zinc-400">in #{bm.channelName}</span>
                        <span className="text-[10px] text-zinc-400">{new Date(bm.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                      </div>
                      <p className="text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2">
                        {bm.content.replace(/<@[^>]+>/g, (m) => { const n = m.match(/:(.+)>/); return n ? `@${n[1]}` : '@someone'; }).replace(/::cmd::.*/g, '[Command]').replace(/::resource::.*/g, '[Resource]').replace(/::recording::.*/g, '[Recording]').substring(0, 150)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBookmark({ id: bm.id, channelId: bm.channelId, content: bm.content, sender: { name: bm.senderName } } as any);
                      }}
                      className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 flex-shrink-0"
                      title="Remove bookmark"
                    >
                      <X className="w-3 h-3 text-zinc-400 hover:text-red-500" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Search bar */}
          {showSearch && (
            <div className="px-4 py-2 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50 flex items-center gap-2">
              <Search className="w-4 h-4 text-zinc-400 flex-shrink-0" />
              <input
                value={searchQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setSearchQuery(q);
                  if (!q.trim() || !activeChannelId) { setSearchResults([]); return; }
                  const query = q.toLowerCase();
                  const filtered = messages.filter(m =>
                    (m.content || "").toLowerCase().includes(query) ||
                    (m.sender?.name || "").toLowerCase().includes(query)
                  );
                  setSearchResults(filtered);
                }}
                placeholder="Search messages in this channel..."
                className="flex-1 bg-transparent text-sm border-none outline-none placeholder:text-zinc-400 text-zinc-700 dark:text-zinc-200"
                autoFocus
              />
              {searchQuery && (
                <span className="text-xs text-zinc-400">{searchResults.length} found</span>
              )}
              <button onClick={() => { setShowSearch(false); setSearchQuery(""); setSearchResults([]); }} className="p-1 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700">
                <X className="w-3.5 h-3.5 text-zinc-400" />
              </button>
            </div>
          )}

          {/* Messages (virtualized) */}
          <VirtualizedMessageList
            hasOlder={!!hasNextPage}
            loadingOlder={!!isFetchingNextPage}
            onLoadOlder={fetchNextPage}
            messages={messages}
            searchQuery={searchQuery}
            searchResults={searchResults}
            messagesLoading={messagesLoading}
            messagesContainerRef={messagesContainerRef}
            userId={userId}
            activeChannelId={activeChannelId}
            queryClient={queryClient}
            reactMutation={reactMutation}
            deleteMutation={deleteMutation}
            editMutation={editMutation}
            onReply={(msg) => {
              setReplyingTo({ id: msg.id, senderName: msg.sender?.name || "someone", content: msg.content || "" });
              inputRef.current?.focus();
            }}
            onViewThread={(msgId) => setThreadParentId(msgId)}
            onBookmark={handleBookmark}
            onForward={handleForward}
            bookmarkedIds={bookmarkedIds}
            readReceipts={readReceipts}
            allMembers={members.map(m => ({ userId: m.userId, name: m.user.name || m.user.email, profileImageUrl: m.user.profileImageUrl, agencyRole: m.agencyRole, agencyName: m.agencyName || agencyName }))}
            isAdminOrOwner={isOwnerOrAdmin}
            unreadCount={activeChannel?.unreadCount || 0}
            highlightedMessageId={highlightedMessageId}
            onSendGifReply={activeChannelId ? (gifUrl, parentMessageId) => sendReplyMutation.mutate({ channelId: activeChannelId, content: gifUrl, parentMessageId }) : undefined}
          />

          {/* Typing indicator */}
          <div aria-live="polite" aria-atomic="true">
            <TypingIndicator users={typingUsers} />
          </div>

          {/* Ephemeral card — private command result (only you see) */}
          {ephemeralCard && (
            <div className="px-4 pt-2">
              <div className="relative">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Only visible to you</span>
                  <button onClick={() => setEphemeralCard(null)} className="ml-auto text-zinc-400 hover:text-zinc-600 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                {(() => {
                  try {
                    const cmd = JSON.parse(ephemeralCard.replace("::cmd::", ""));
                    const brandBg = 'var(--primary, #3b82f6)';

                    if (cmd.type === "mystats") {
                      const statItems = [
                        { label: "Policies Sold", value: cmd.policies || cmd.sales, Icon: Trophy, bg: "bg-amber-50 dark:bg-amber-900/20", iconColor: "text-amber-500", color: "text-amber-600 dark:text-amber-400" },
                        { label: "Premium", value: `$${(cmd.premium || 0).toLocaleString()}`, Icon: DollarSign, bg: "bg-emerald-50 dark:bg-emerald-900/20", iconColor: "text-emerald-500", color: "text-emerald-600 dark:text-emerald-400" },
                        { label: "Calls Made", value: cmd.calls, Icon: PhoneCall, bg: "bg-blue-50 dark:bg-blue-900/20", iconColor: "text-blue-500", color: "text-blue-600 dark:text-blue-400" },
                        { label: "Appointments", value: cmd.appointments, Icon: Calendar, bg: "bg-purple-50 dark:bg-purple-900/20", iconColor: "text-purple-500", color: "text-purple-600 dark:text-purple-400" },
                        { label: "Leads", value: cmd.leads, Icon: Users, bg: "bg-cyan-50 dark:bg-cyan-900/20", iconColor: "text-cyan-500", color: "text-cyan-600 dark:text-cyan-400" },
                        { label: "Conv Rate", value: `${(cmd.convRate || cmd.conversion || 0).toFixed(1)}%`, Icon: TrendingUp, bg: "bg-orange-50 dark:bg-orange-900/20", iconColor: "text-orange-500", color: "text-orange-600 dark:text-orange-400" },
                        { label: "Close Rate", value: `${(cmd.closeRate || 0).toFixed(1)}%`, Icon: Target, bg: "bg-rose-50 dark:bg-rose-900/20", iconColor: "text-rose-500", color: "text-rose-600 dark:text-rose-400" },
                        { label: "Avg Premium", value: `$${(cmd.avgPremium || 0).toLocaleString()}`, Icon: DollarSign, bg: "bg-indigo-50 dark:bg-indigo-900/20", iconColor: "text-indigo-500", color: "text-indigo-600 dark:text-indigo-400" },
                      ];
                      return (
                        <div className="max-w-sm">
                          <div className="rounded-2xl overflow-hidden shadow-lg border border-zinc-200 dark:border-zinc-700">
                            <div className="px-4 py-3 flex items-center gap-2" style={{ background: brandBg }}>
                              <BarChart3 className="w-5 h-5 text-white" />
                              <div>
                                <span className="text-white font-bold text-sm">{cmd.name}&apos;s Stats</span>
                                {cmd.rank > 0 && <span className="ml-2 text-[10px] bg-white/20 text-white px-1.5 py-0.5 rounded-full font-bold">Rank #{cmd.rank}</span>}
                              </div>
                              <span className="ml-auto text-[10px] text-white/70 font-medium">{cmd.period}</span>
                            </div>
                            <div className="bg-white dark:bg-zinc-800 grid grid-cols-2 gap-px bg-zinc-100 dark:bg-zinc-700">
                              {statItems.map((s, i) => (
                                <div key={i} className="bg-white dark:bg-zinc-800 px-3 py-2.5 flex items-center gap-2.5">
                                  <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
                                    <s.Icon className={`w-4 h-4 ${s.iconColor}`} />
                                  </div>
                                  <div>
                                    <p className={`text-base font-black ${s.color}`}>{s.value}</p>
                                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider font-semibold">{s.label}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (cmd.type === "pipeline") {
                      const items = [
                        { label: "Leads", value: cmd.leads, Icon: Users, bg: "bg-blue-500" },
                        { label: "Appointments", value: cmd.appointments, Icon: Calendar, bg: "bg-purple-500" },
                        { label: "Calls Made", value: cmd.calls, Icon: PhoneCall, bg: "bg-cyan-500" },
                        { label: "Policies Sold", value: cmd.sold, Icon: Target, bg: "bg-emerald-500" },
                      ];
                      return (
                        <div className="max-w-sm">
                          <div className="rounded-2xl overflow-hidden shadow-lg border border-zinc-200 dark:border-zinc-700">
                            <div className="px-4 py-3 flex items-center gap-2" style={{ background: brandBg }}>
                              <span className="text-lg">{"\uD83D\uDCCB"}</span>
                              <span className="text-white font-bold text-sm">Pipeline Summary</span>
                              <span className="ml-auto text-[10px] text-white/70 font-medium">{cmd.period}</span>
                            </div>
                            <div className="bg-white dark:bg-zinc-800 px-4 py-3 space-y-2.5">
                              {items.map((item, i) => (
                                <div key={i} className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-lg ${item.bg} flex items-center justify-center`}><item.Icon className="w-4 h-4 text-white" /></div>
                                  <p className="flex-1 text-xs text-zinc-400 uppercase tracking-wider font-semibold">{item.label}</p>
                                  <p className="text-lg font-black text-zinc-800 dark:text-zinc-200">{(item.value || 0).toLocaleString()}</p>
                                </div>
                              ))}
                              {cmd.premium > 0 && (
                                <>
                                  <div className="h-px bg-zinc-100 dark:bg-zinc-700" />
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-zinc-400 font-semibold">Total Premium</span>
                                    <span className="text-lg font-black text-emerald-500">${(cmd.premium || 0).toLocaleString()}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return null;
                  } catch {
                    // Not JSON — render as plain text (e.g. /help output)
                    return (
                      <div className="bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 px-4 py-3 max-w-sm">
                        <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">{ephemeralCard}</p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          )}

          {/* Reply-to banner (Discord-style) */}
          {replyingTo && (
            <div className="px-4 py-2 bg-zinc-50 dark:bg-zinc-900/50">
              <div className="flex items-start gap-2 pl-3 border-l-2 border-blue-500 rounded-r-lg bg-white dark:bg-zinc-800/60 px-3 py-2 shadow-sm">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <CornerDownRight className="w-3 h-3 text-blue-500 flex-shrink-0" />
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                      Replying to {replyingTo.senderName}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 line-clamp-2 pl-[18px]">
                    {replyingTo.content
                      .replace(/<@[^>]+>/g, (m) => { const n = m.match(/:(.+)>/); return n ? `@${n[1]}` : '@someone'; })
                      .replace(/::cmd::.*/g, '[Command]')
                      .replace(/::resource::.*/g, '[Resource]')
                      .replace(/::recording::.*/g, '[Recording]')
                      .slice(0, 200)}
                  </p>
                </div>
                <button onClick={() => setReplyingTo(null)} className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 flex-shrink-0 mt-0.5">
                  <X className="w-3.5 h-3.5 text-zinc-400" />
                </button>
              </div>
            </div>
          )}

          {/* Pending file preview */}
          {pendingFile && (
            <div className="px-4 py-2 bg-zinc-50 dark:bg-zinc-900/50 border-t border-zinc-100 dark:border-zinc-800/50">
              <div className="flex items-center gap-3 bg-white dark:bg-zinc-800/60 rounded-lg px-3 py-2 shadow-sm">
                {pendingFilePreview ? (
                  <img src={pendingFilePreview} alt={pendingFile.name} className="w-16 h-16 rounded-md object-cover flex-shrink-0" />
                ) : pendingFile.type.startsWith('video/') ? (
                  <div className="w-16 h-16 rounded-md bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center flex-shrink-0">
                    <Play className="w-6 h-6 text-zinc-500" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-md bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-zinc-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200 truncate">{pendingFile.name}</p>
                  <p className="text-[10px] text-zinc-400">{(pendingFile.size / 1024).toFixed(0)} KB{pendingFile.type.startsWith('image/') ? ' · Image' : pendingFile.type.startsWith('video/') ? ' · Video' : ''}</p>
                </div>
                <button
                  onClick={() => {
                    setPendingFile(null);
                    if (pendingFilePreview) { URL.revokeObjectURL(pendingFilePreview); setPendingFilePreview(null); }
                  }}
                  className="p-1.5 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-700 flex-shrink-0"
                  title="Remove file"
                >
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              </div>
            </div>
          )}

          {/* Message input */}
          {activeChannelId && isChannelArchived && (
            <div className="px-4 py-3 border-t border-zinc-100 dark:border-zinc-800/50 bg-amber-50/50 dark:bg-amber-950/10 flex items-center justify-center gap-2">
              <Lock className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-amber-600 dark:text-amber-400 font-medium">This channel is archived and read-only</span>
            </div>
          )}
          {activeChannelId && !isChannelArchived && !canPostInChannel && (
            <div className="px-4 py-3 border-t border-zinc-100 dark:border-zinc-800/50 bg-blue-50/50 dark:bg-blue-950/10 flex items-center justify-center gap-2">
              <Megaphone className="w-4 h-4 text-blue-500" />
              <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">Only agency owners can post in this channel</span>
            </div>
          )}
          {activeChannelId && !isChannelArchived && canPostInChannel && (
            <div className="px-4 pb-4 pt-2 border-t border-zinc-100 dark:border-zinc-800/50">
              {isRecordingVoice ? (
                <VoiceRecorder
                  onSend={async (blob) => {
                    setIsRecordingVoice(false);
                    const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
                    await uploadMutation.mutateAsync({ channelId: activeChannelId!, file });
                  }}
                  onCancel={() => setIsRecordingVoice(false)}
                />
              ) : (
                <div className="relative flex items-end gap-2 bg-zinc-100 dark:bg-zinc-800/50 rounded-xl px-3 py-2">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*,audio/*,video/mp4,video/webm,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 15 * 1024 * 1024) {
                        setUploadError(`File is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max size is 15MB.`);
                        setTimeout(() => setUploadError(null), 5000);
                        e.target.value = "";
                        return;
                      }
                      setPendingFile(file);
                      // Generate preview for images
                      if (file.type.startsWith('image/')) {
                        const url = URL.createObjectURL(file);
                        setPendingFilePreview(url);
                      } else {
                        setPendingFilePreview(null);
                      }
                      e.target.value = "";
                      // Focus the input so they can type a message
                      setTimeout(() => inputRef.current?.focus(), 100);
                    }}
                  />

                  {/* LEFT — Attach & Resources */}
                  <div className="flex items-center gap-0.5 flex-shrink-0 mb-0.5">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-1.5 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                      title="Attach file"
                    >
                      <Paperclip className="w-5 h-5 text-zinc-400" />
                    </button>
                    <Popover open={resourcePickerOpen} onOpenChange={setResourcePickerOpen}>
                      <PopoverTrigger asChild>
                        <button className="p-1.5 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors" title="Share a resource">
                          <FolderOpen className="w-5 h-5 text-zinc-400" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" side="top" align="start" sideOffset={12}>
                        <ResourcePicker onSelect={(resource) => {
                          if (!activeChannelId) return;
                          const msg = `::resource::${JSON.stringify({
                            title: resource.title,
                            description: resource.description,
                            category: resource.category,
                            carrier: resource.carrier,
                            scope: resource.scope,
                            shareUrl: resource.fileUrl || "",
                            id: resource.id,
                          })}`;
                          sendMutation.mutate({ channelId: activeChannelId, content: msg });
                          setResourcePickerOpen(false);
                        }} />
                      </PopoverContent>
                    </Popover>
                    <Popover open={recordingPickerOpen} onOpenChange={setRecordingPickerOpen}>
                      <PopoverTrigger asChild>
                        <button className="p-1.5 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors" title="Share a call recording">
                          <Phone className="w-5 h-5 text-zinc-400" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" side="top" align="start" sideOffset={12}>
                        <RecordingPicker onSelect={(call) => {
                          if (!activeChannelId) return;
                          const msg = `::recording::${JSON.stringify({
                            leadName: call.leadName,
                            phone: call.phone,
                            date: call.date,
                            duration: call.duration,
                            direction: call.direction,
                            recordingUrl: call.recordingUrl,
                            callSid: call.callSid || "",
                          })}`;
                          sendMutation.mutate({ channelId: activeChannelId, content: msg });
                          setRecordingPickerOpen(false);
                        }} />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* @Mention autocomplete */}
                  {mentionSearch && !showSlashMenu && (
                    <MentionAutocomplete
                      query={mentionSearch.query}
                      members={members}
                      onSelect={insertMention}
                      selectedIndex={mentionSelectedIndex}
                      isOwnerOrAdmin={isOwnerOrAdmin}
                      isAnnouncementChannel={isAnnouncementChannel}
                      agencyList={isAnnouncementChannel ? agencyList : undefined}
                      onSelectAgency={insertAgencyMention}
                    />
                  )}

                  {/* Slash command menu */}
                  {showSlashMenu && filteredCommands.length > 0 && (
                    <div className="absolute bottom-full left-0 right-0 mb-2 mx-3 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-xl overflow-hidden z-20">
                      <div className="px-3 py-1.5 border-b border-zinc-100 dark:border-zinc-700">
                        <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Commands</span>
                      </div>
                      {filteredCommands.map(cmd => (
                        <button
                          key={cmd.cmd}
                          onClick={() => { executeSlashCommand(cmd.cmd); }}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-700/50 transition-colors text-left"
                        >
                          <span className="text-lg">{cmd.emoji}</span>
                          <div>
                            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{cmd.cmd}</p>
                            <p className="text-xs text-zinc-400">{cmd.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* CENTER — Text input */}
                  <Textarea
                    ref={inputRef}
                    value={messageInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    aria-label={`Message input for ${activeChannel?.channel.type === "dm" ? "direct message" : (activeChannel?.channel.name || "channel")}`}
                    placeholder={`Message #${activeChannel?.channel.type === "dm" ? "DM" : (activeChannel?.channel.name || "channel")}${
                      // Shell: the "(type @ to mention)" tail wrapped the
                      // placeholder to two clipped lines on a phone
                      typeof window !== "undefined" && window.location.pathname.startsWith("/m")
                        ? ""
                        : "... (type @ to mention)"
                    }`}
                    className="flex-1 bg-transparent border-none shadow-none resize-none text-sm min-h-[36px] max-h-[120px] py-1.5 px-1 focus-visible:ring-0 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                    rows={1}
                  />

                  {/* RIGHT — one button for emoji, GIFs and stickers (the panel
                      switches between the three), then Mic/Send. */}
                  <div className="flex items-center gap-0.5 flex-shrink-0 mb-0.5">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className="p-1.5 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                          title="Emoji, GIFs and stickers"
                          aria-label="Emoji, GIFs and stickers"
                        >
                          <Smile className="w-5 h-5 text-zinc-400" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" side="top" align="end">
                        <MediaPicker
                          onEmoji={emoji => {
                            setMessageInput(prev => prev + emoji);
                            inputRef.current?.focus();
                          }}
                          onGif={url => {
                            if (activeChannelId) {
                              sendMutation.mutate({ channelId: activeChannelId, content: url });
                            }
                          }}
                          onSticker={url => {
                            if (activeChannelId) {
                              // Tagged so the renderer sizes it as a sticker,
                              // not as a full-width GIF.
                              sendMutation.mutate({ channelId: activeChannelId, content: `::sticker::${url}` });
                            }
                          }}
                          onGenerateSticker={async file => {
                            if (!activeChannelId) return;
                            // Straight through the normal attachment upload, so
                            // an AI sticker is stored and rendered exactly like
                            // any other image — no second storage path.
                            // Tagged so it renders as a sticker rather than a
                            // file card with the generated filename on top.
                            await teamChatService.uploadFile(activeChannelId, file, "::sticker::");
                            queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] });
                          }}
                          onClip={url => {
                            if (activeChannelId) {
                              // mp4, so it needs its own tag and a <video>.
                              sendMutation.mutate({ channelId: activeChannelId, content: `::clip::${url}` });
                            }
                          }}
                          onMeme={url => {
                            if (activeChannelId) {
                              // Own tag: a meme is a png/webp, which the GIF
                              // renderer's extension match would miss, and it
                              // needs to be big enough to read the caption.
                              sendMutation.mutate({ channelId: activeChannelId, content: `::meme::${url}` });
                            }
                          }}
                        />
                      </PopoverContent>
                    </Popover>

                    {messageInput.trim() ? (
                      <button
                        onClick={handleSend}
                        disabled={sendMutation.isPending}
                        className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-30 transition-colors"
                        title="Send"
                      >
                        {sendMutation.isPending ? (
                          <Loader2 className="w-4 h-4 text-white animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 text-white" />
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsRecordingVoice(true)}
                        className="p-2 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                        title="Voice message"
                      >
                        <Mic className="w-4 h-4 text-zinc-400" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Upload error */}
              {uploadError && (
                <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                  <span>{uploadError}</span>
                  <button onClick={() => setUploadError(null)} className="ml-auto hover:text-red-300">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Upload progress */}
              {uploadMutation.isPending && (
                <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Uploading...
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right — Thread panel (takes priority over members) */}
        {threadParentId && threadParentMessage ? (
          <div className="hidden lg:flex lg:flex-col w-80 flex-shrink-0 border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            {/* Thread header */}
            <div className="flex items-center gap-2 px-4 h-12 border-b border-zinc-200 dark:border-zinc-800 flex-shrink-0">
              <CornerDownRight className="w-4 h-4 text-blue-500" />
              <span className="font-semibold text-sm text-zinc-800 dark:text-zinc-200">Replies</span>
              <button
                onClick={() => { setThreadParentId(null); setThreadInput(""); }}
                className="ml-auto p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            {/* Parent message */}
            <div className="border-b border-zinc-100 dark:border-zinc-800/50">
              <MessageItem
                message={threadParentMessage}
                isOwn={threadParentMessage.senderId === userId}
                currentUserId={userId}
                onReact={emoji => {
                  const existingReaction = (threadParentMessage.reactions || []).find(
                    (r: any) => r.emoji === emoji && r.userId === userId
                  );
                  if (existingReaction) {
                    teamChatService.removeReaction(threadParentMessage.id, emoji).then(() => {
                      queryClient.invalidateQueries({ queryKey: ["team-chat", "messages", activeChannelId] });
                    });
                  } else {
                    reactMutation.mutate({ messageId: threadParentMessage.id, emoji });
                  }
                }}
                onDelete={() => {}}
                onEdit={() => {}}
              />
              {(threadData?.messages?.length || 0) > 0 && (
                <div className="px-4 py-1.5">
                  <span className="text-xs text-zinc-400 dark:text-zinc-500">
                    {threadData!.messages.length} {threadData!.messages.length === 1 ? 'reply' : 'replies'}
                  </span>
                </div>
              )}
            </div>

            {/* Thread replies */}
            <div className="flex-1 overflow-y-auto">
              {(threadData?.messages || []).map(reply => (
                <MessageItem
                  key={reply.id}
                  message={reply}
                  isOwn={reply.senderId === userId}
                  currentUserId={userId}
                  onReact={emoji => {
                    const existingReaction = (reply.reactions || []).find(
                      (r: any) => r.emoji === emoji && r.userId === userId
                    );
                    if (existingReaction) {
                      teamChatService.removeReaction(reply.id, emoji).then(() => {
                        queryClient.invalidateQueries({ queryKey: ["team-chat", "thread", threadParentId] });
                      });
                    } else {
                      reactMutation.mutate({ messageId: reply.id, emoji });
                    }
                  }}
                  onDelete={() => {
                    deleteMutation.mutate(reply.id);
                    queryClient.invalidateQueries({ queryKey: ["team-chat", "thread", threadParentId] });
                  }}
                  isAdminOrOwner={isOwnerOrAdmin}
                  onEdit={content => {
                    editMutation.mutate({ messageId: reply.id, content });
                    queryClient.invalidateQueries({ queryKey: ["team-chat", "thread", threadParentId] });
                  }}
                  onSendGif={activeChannelId ? (gifUrl) => sendReplyMutation.mutate({ channelId: activeChannelId, content: gifUrl, parentMessageId: reply.id }) : undefined}
                />
              ))}
            </div>

            {/* Thread reply input */}
            <div className="px-3 pb-3 pt-2 border-t border-zinc-100 dark:border-zinc-800/50">
              <div className="flex items-end gap-2 bg-zinc-100 dark:bg-zinc-800/50 rounded-xl px-3 py-2">
                <Textarea
                  value={threadInput}
                  onChange={e => setThreadInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleThreadSend();
                    }
                  }}
                  placeholder="Reply in thread..."
                  className="flex-1 bg-transparent border-none shadow-none resize-none text-sm min-h-[36px] max-h-[80px] py-1.5 px-1 focus-visible:ring-0 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                  rows={1}
                />
                <button
                  onClick={handleThreadSend}
                  disabled={!threadInput.trim() || sendReplyMutation.isPending}
                  className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-30 transition-colors flex-shrink-0"
                >
                  {sendReplyMutation.isPending ? (
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 text-white" />
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : showMembers && activeChannelId && isAnnouncementChannel && !isOwnerOrAdmin ? (
          /* Agents see a notice instead of member list in announcement channels */
          <div className="hidden lg:flex lg:flex-col w-56 flex-shrink-0 border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 items-center justify-center text-center px-4">
            <Lock className="w-8 h-8 text-zinc-300 dark:text-zinc-600 mb-2" />
            <p className="text-xs font-semibold text-zinc-400 dark:text-zinc-500">Agency Owners Only</p>
            <p className="text-[10px] text-zinc-400 dark:text-zinc-600 mt-1">Member list is restricted to agency owners and admins in this channel</p>
          </div>
        ) : showMembers && activeChannelId ? (
          /* Right — Members panel */
          <div className="hidden lg:block w-56 flex-shrink-0">
            <MemberList members={members} presenceMap={presenceMap} onStartDm={handleStartDm} isAnnouncementChannel={isAnnouncementChannel} isDm={activeChannel?.channel.type === "dm"} agencyList={agencyList} />
          </div>
        ) : null}
      </div>

      {/* Members as a RIGHT drawer below lg (Discord-iOS style) — the inline
          panes above are hidden on phone/tablet, this is their counterpart. */}
      <Sheet open={mobileMembersOpen} onOpenChange={setMobileMembersOpen}>
        <SheetContent side="right" className="team-chat-drawer w-72 p-0">
          {activeChannelId && isAnnouncementChannel && !isOwnerOrAdmin ? (
            <div className="flex h-full flex-col items-center justify-center px-4 text-center">
              <Lock className="w-8 h-8 text-zinc-300 dark:text-zinc-600 mb-2" />
              <p className="text-xs font-semibold text-zinc-400 dark:text-zinc-500">Agency Owners Only</p>
              <p className="text-[10px] text-zinc-400 dark:text-zinc-600 mt-1">Member list is restricted to agency owners and admins in this channel</p>
            </div>
          ) : activeChannelId ? (
            <div className="h-full overflow-y-auto pt-2">
              <MemberList members={members} presenceMap={presenceMap} onStartDm={(...args) => { setMobileMembersOpen(false); return handleStartDm(...args); }} isAnnouncementChannel={isAnnouncementChannel} isDm={activeChannel?.channel.type === "dm"} agencyList={agencyList} />
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Forward message dialog */}
      <Dialog open={!!forwardingMessage} onOpenChange={(open) => { if (!open) setForwardingMessage(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Forward className="w-5 h-5 text-blue-500" />
              Forward Message
            </DialogTitle>
          </DialogHeader>
          <div className="my-2 px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700">
            <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">{forwardingMessage?.sender?.name}</p>
            <p className="text-sm text-zinc-700 dark:text-zinc-300 line-clamp-3">
              {(forwardingMessage?.content || "")
                .replace(/<@[^>]+>/g, (m: string) => { const n = m.match(/:(.+)>/); return n ? `@${n[1]}` : '@someone'; })
                .replace(/::cmd::.*/g, '[Command]')
                .replace(/::resource::.*/g, '[Resource]')
                .replace(/::recording::.*/g, '[Recording]')
                .slice(0, 200) || "[Attachment]"}
            </p>
          </div>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider px-1 mb-2">Select a channel</p>
            {channels
              .filter(c => c.channel.type === "channel" && c.channel.id !== activeChannelId && !c.channel.description?.startsWith('[ARCHIVED]'))
              .map(c => (
                <button
                  key={c.channel.id}
                  onClick={() => {
                    if (!forwardingMessage) return;
                    const senderName = forwardingMessage.sender?.name || "someone";
                    const fromChannel = activeChannel?.channel.name || "unknown";
                    const originalContent = (forwardingMessage.content || "").slice(0, 2000);
                    const fwdContent = `Forwarded from #${fromChannel} by @${senderName}:\n> ${originalContent}`;
                    sendMutation.mutate({ channelId: c.channel.id, content: fwdContent });
                    setForwardingMessage(null);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <Hash className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                  <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{c.channel.name}</span>
                </button>
              ))}
            {channels.filter(c => c.channel.type === "channel" && c.channel.id !== activeChannelId && !c.channel.description?.startsWith('[ARCHIVED]')).length === 0 && (
              <p className="text-xs text-zinc-400 text-center py-4">No other channels available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create channel dialog */}
      <Dialog open={showCreateChannel} onOpenChange={setShowCreateChannel}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Channel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 block">
                Channel Name
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  value={newChannelName}
                  onChange={e => setNewChannelName(e.target.value)}
                  placeholder="e.g. team-leads, training"
                  className="pl-9"
                  maxLength={100}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 block">
                Description <span className="text-zinc-400 font-normal">(optional)</span>
              </label>
              <Input
                value={newChannelDesc}
                onChange={e => setNewChannelDesc(e.target.value)}
                placeholder="What's this channel about?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateChannel(false)}>Cancel</Button>
            <Button
              onClick={() => createChannelMutation.mutate({ name: newChannelName, description: newChannelDesc })}
              disabled={!newChannelName.trim() || createChannelMutation.isPending}
            >
              {createChannelMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Channel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shimmer animation keyframes */}
      <style jsx global>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes highlight-flash {
          0% { background-color: rgba(251, 191, 36, 0.3); }
          30% { background-color: rgba(251, 191, 36, 0.15); }
          100% { background-color: transparent; }
        }
      `}</style>
    </>
  );
}
